import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createAudioContext } from '../audioContext';
import { runMhrRetrieval, RETRIEVAL_STEPS, DEMO_IHI } from '../services/mhrClient';
import EmergencyAccessDialog from './EmergencyAccessDialog';
import InsightMarkdown from './InsightMarkdown';

const POSE_CONNECTIONS = [
  [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 23], [12, 24], [23, 24], [23, 25], [24, 26],
  [25, 27], [26, 28]
];

const btnStyle = {
  padding: '5px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
  background: '#2a2a3a', color: '#ccc', fontSize: '12px', fontWeight: '500', width: '100%',
  marginBottom: '6px', transition: 'background 0.15s'
};

function getAuthToken() {
  try {
    const stored = localStorage.getItem('matilda_auth');
    if (stored) return JSON.parse(stored).token;
  } catch {}
  return null;
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

function ClinicianConsole() {
  const videoImgRef = useRef(null);
  // Panels are JPEGs on a link that may be satellite, so Python only produces
  // them while a clinician actually has the view open.
  const toggleDiagPanels = async (on) => {
    setDiagPanelsOn(on);
    try {
      await fetch('/api/overlay-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ diag_panels: on })
      });
    } catch { /* the panels just stay empty; nothing else depends on this */ }
  };
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const pendingFrameRef = useRef(null);
  const frameRafRef = useRef(null);
  const overlayDataRef = useRef({ pose: null, faceBbox: null, handBbox: null, breathing: null });
  const overlayRafRef = useRef(null);
  const audioRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const audioQueueRef = useRef([]);

  // Clinician mic capture
  const micAudioCtxRef = useRef(null);
  const micPcm16NodeRef = useRef(null);
  const micSourceNodeRef = useRef(null);
  const micStreamRef = useRef(null);
  const micActiveRef = useRef(false);

  // Audio playback for patient audio
  const playbackCtxRef = useRef(null);
  const playbackNextTimeRef = useRef(0);

  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('matilda_auth');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [googleClientId, setGoogleClientId] = useState(null);
  // Server-declared: is the local dev sign-in bypass available? Never set here.
  const [devAuthAvailable, setDevAuthAvailable] = useState(false);

  const [connectionStatus, setConnectionStatus] = useState('waiting');
  // True only when the patient explicitly tapped "Leave call" — lets the
  // console distinguish a hang-up from a connection dropout.
  const [patientLeftDeliberately, setPatientLeftDeliberately] = useState(false);
  // SMS invite link
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null); // { ok, message }
  // Who gets paged when this consult escalates. Prefilled with the on-call
  // default so escalation never stalls waiting for someone to type a number,
  // but editable because the roster changes shift to shift.
  // Remembered per browser: the roster changes shift to shift, and retyping it
  // every time the console reloads is how it ends up wrong at 3am.
  // Versioned: the previous default is already cached in any console that has
  // opened this panel, so changing the default alone would leave them silently
  // paging the old number. Bumping the key re-seeds everyone once; anything
  // they type after that persists as before.
  const ONCALL_PHONE_KEY = 'matilda.oncallPhone.v2';
  const [oncallPhone, setOncallPhone] = useState(() => {
    try { return localStorage.getItem(ONCALL_PHONE_KEY) || '0459 567 974'; }
    catch { return '0459 567 974'; }
  });
  useEffect(() => {
    try { localStorage.setItem(ONCALL_PHONE_KEY, oncallPhone); } catch { /* private mode */ }
  }, [oncallPhone]);
  const [oncallOpen, setOncallOpen] = useState(false);

  // Medical chest custodian — the other number an escalation dials, and set at
  // the same moment as the on-call doctor. Same persistence, same default
  // behaviour: the server falls back to its own if this is left blank.
  const MEDCHEST_PHONE_KEY = 'matilda.medchestPhone';
  const [medchestPhone, setMedchestPhone] = useState(() => {
    try { return localStorage.getItem(MEDCHEST_PHONE_KEY) || '0498 231 894'; }
    catch { return '0498 231 894'; }
  });
  useEffect(() => {
    try { localStorage.setItem(MEDCHEST_PHONE_KEY, medchestPhone); } catch { /* private mode */ }
  }, [medchestPhone]);
  const [oncallSending, setOncallSending] = useState(false);
  const [oncallResult, setOncallResult] = useState(null);
  // Escalation summary for the subtle video overlay
  const [escalationNotice, setEscalationNotice] = useState(null); // { clinician, atsLabel, triageScore }
  const [escalationEta, setEscalationEta] = useState(null);
  // Aeromedical dispatch tracker
  const [flight, setFlight] = useState(null);
  // The patient's ACTUAL mic state, as reported by their client — not the last
  // command we sent. The patient can always unmute themselves, so tracking our
  // own intent here would desync the button.
  const [patientMic, setPatientMic] = useState({ muted: false, by: 'patient' });
  // Whether a patient socket is attached. Distinct from connectionStatus, which
  // only reaches 'live' once video frames arrive — a patient with their camera
  // off is still present and still audible, and must still be mutable.
  const [patientPresent, setPatientPresent] = useState(false);
  const [confirmEndCall, setConfirmEndCall] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  // Single-use invite link minted for this room, shown in the Session panel.
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  // Clinician mic is muted by default. Auto-unmutes when the clinician takes over the consult,
  // and re-mutes on hand-back. The Audio button still acts as a manual override either way.
  const [audioMuted, setAudioMuted] = useState(true);
  const [vitals, setVitals] = useState({ bpm: 0, breathingRate: null, bmi: null, bp: null, spo2: null });
  // Per-vital last-updated timestamps (ms epoch). Used to show "Xs ago" freshness next
  // to each value and to fade out stale readings.
  const [vitalUpdatedAt, setVitalUpdatedAt] = useState({ hr: null, rr: null, spo2: null });
  // Warm-up state reported by the Python pipeline: each of HR and breathing is
  // "settled" only after it has been producing values for the warm-up period and
  // has held steady. Until then the reading is shown as calibrating and kept out
  // of everything clinical — an unsettled estimate reads low, and a low HR that
  // reaches triage gets treated as bradycardia.
  const [vitalsStatus, setVitalsStatus] = useState(null);
  // When the patient went live — used as the fallback calibration window before
  // the pipeline has reported any warm-up state.
  const liveStartTimeRef = useRef(null);
  // Tick counter to force vitals freshness/age labels to re-render every few seconds.
  const [vitalsTick, setVitalsTick] = useState(0);
  const [emotion, setEmotion] = useState({ dominant: null, age: null, gender: null });
  // The rPPG intermediate stages. Python has always computed these; they were
  // baked into an OpenCV composite and dropped before reaching a browser.
  const [diag, setDiag] = useState(null);
  const [spectrum, setSpectrum] = useState({ frequencies: [], amplitudes: [] });
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagPanelsOn, setDiagPanelsOn] = useState(false);
  const [frameDescription, setFrameDescription] = useState('');
  const [triageScore, setTriageScore] = useState(null);
  const [triageReasoning, setTriageReasoning] = useState('');
  const [triageConfidence, setTriageConfidence] = useState(null);
  const [triageHistory, setTriageHistory] = useState([]);

  // Escalation agent (triggers on ATS 1-3, re-triggers if severity increases)
  const [escalationActive, setEscalationActive] = useState(false);
  const [escalationSteps, setEscalationSteps] = useState([]);
  const [escalationDismissed, setEscalationDismissed] = useState(false);
  const escalatedScoreRef = useRef(null); // most-severe score we've already escalated for
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [autoAnalysis, setAutoAnalysis] = useState(false);
  const [matildaVoice, setMatildaVoice] = useState(true);
  const [latestFrame, setLatestFrame] = useState(null);
  const [showPose, setShowPose] = useState(true);
  const [showHrBox, setShowHrBox] = useState(true);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mainView, setMainView] = useState('live');

  // Patient identity verification (server-driven, GPT-4o-mini periodic check)
  const [identityThumbnail, setIdentityThumbnail] = useState(null);
  const [identityStatus, setIdentityStatus] = useState('enrolling'); // enrolling | checking | verified | absent | mismatch | unknown | error
  const [identityAbsentSeconds, setIdentityAbsentSeconds] = useState(0);
  const [identityConfidence, setIdentityConfidence] = useState(null);
  const [identityReason, setIdentityReason] = useState(null);
  const [identityCheckedAt, setIdentityCheckedAt] = useState(null);
  const [identityFailures, setIdentityFailures] = useState(0);

  // Insight chat
  const [insightOpen, setInsightOpen] = useState(false);
  const [insightMessages, setInsightMessages] = useState([]);
  const [insightInput, setInsightInput] = useState('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightCopied, setInsightCopied] = useState(null);
  const insightEndRef = useRef(null);
  const insightInputRef = useRef(null);

  // RDR Adult Observation Chart
  const [rdrObservations, setRdrObservations] = useState([]);
  const [rdrExpanded, setRdrExpanded] = useState(true);
  const lastRdrRef = useRef(null);
  const [llmVitals, setLlmVitals] = useState({ gcs: null, avpu: null, painScore: null, newPain: null, sedationScore: null, temperature: null, bgl: null });
  const llmVitalsTimestamp = useRef(null);

  // Belief-Guided Vision (BGV)
  const [bgvEnabled, setBgvEnabled] = useState(true);
  const [bgvState, setBgvState] = useState({
    initialized: false, frameCount: 0,
    structure: null, dynamics: null,
    precision: { structure: 0.5, dynamics: 0.5 },
    lastObservation: null, changesDetected: null, surpriseLevel: 'low', surpriseReason: '',
    prediction: null, predictionAccuracy: null, beliefsSummary: null
  });
  const [bgvExpanded, setBgvExpanded] = useState(true);
  const [healthRecordExpanded, setHealthRecordExpanded] = useState(true);
  const [bgvLoading, setBgvLoading] = useState(false);

  // Health record
  const [healthRecord, setHealthRecord] = useState(null);
  const [healthRecordAnalysis, setHealthRecordAnalysis] = useState(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [hrSectionOpen, setHrSectionOpen] = useState({ allergies: false, medications: false, history: false, immunisations: false });

  // My Health Record FHIR Gateway retrieval
  const [mhrExpanded, setMhrExpanded] = useState(true);
  const [mhrEvents, setMhrEvents] = useState([]);
  const [mhrSource, setMhrSource] = useState(null);
  // The locally-held record, kept SEPARATE from the MHR retrieval. A clinician
  // needs to see which of the two a fact came from — the national record and
  // the practice's own record can disagree, and that disagreement is clinically
  // meaningful rather than something to quietly merge away.
  const [ehrRecord, setEhrRecord] = useState(null);
  const [mhrError, setMhrError] = useState(null);
  const [mhrAccessType, setMhrAccessType] = useState('GeneralAccess');
  // Emergency access overrides the individual's own access controls, so it is
  // gated behind an explicit declaration rather than a single click.
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [emergencyRecording, setEmergencyRecording] = useState(false);
  const [mhrExtra, setMhrExtra] = useState({ documents: [], dispenses: [], pathology: [], pbs: [] });
  const [mhrSectionOpen, setMhrSectionOpen] = useState({ documents: false, dispenses: false, pathology: false, pbs: false });
  const mhrAbortRef = useRef(null);

  // Assessment tools
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [isAssessing, setIsAssessing] = useState(false);
  const [assessmentStep, setAssessmentStep] = useState('');

  // Transcript
  const [transcript, setTranscript] = useState([]);

  const roomId = new URLSearchParams(window.location.search).get('room') || 'default';

  // Fallback calibration window, used only until the pipeline reports warm-up
  // state (older Python builds, or before the first vitals tick arrives).
  const CALIBRATION_FALLBACK_MS = 20000;

  const vitalsSettled = React.useMemo(() => {
    const fallback = liveStartTimeRef.current
      ? Date.now() - liveStartTimeRef.current >= CALIBRATION_FALLBACK_MS
      : false;
    return {
      hr: vitalsStatus?.hr?.settled ?? fallback,
      rr: vitalsStatus?.br?.settled ?? fallback
    };
    // vitalsTick and connectionStatus look unused, but the fallback reads a ref
    // and a wall clock — these are what make it re-evaluate as time passes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vitalsStatus, vitalsTick, connectionStatus]);

  // Vitals as they may be used clinically. HR and RR are withheld until their
  // estimators settle, so a warm-up reading never reaches triage, the frame
  // analyser, the RDR trend or the insight prompt as a real measurement.
  const clinicalVitals = React.useMemo(() => ({
    ...vitals,
    bpm: vitalsSettled.hr ? vitals.bpm : null,
    breathingRate: vitalsSettled.rr ? vitals.breathingRate : null
  }), [vitals, vitalsSettled]);

  // --- Clinician mic capture ---
  const startMicCapture = useCallback(async () => {
    if (micActiveRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Same reasoning as the patient side: the clinician hears the patient
          // and MATILDA through their speakers, and that must not be re-captured.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
          channelCount: 1
        }, video: false
      });
      const audioCtx = createAudioContext({ sampleRate: 24000 });
      if (!audioCtx) return;
      await audioCtx.audioWorklet.addModule('/pcm16-worklet.js');
      const source = audioCtx.createMediaStreamSource(stream);
      const pcm16Node = new AudioWorkletNode(audioCtx, 'pcm16-downsampler');

      pcm16Node.port.onmessage = (event) => {
        const pcm16Buffer = event.data;
        if (pcm16Buffer && pcm16Buffer.byteLength > 0) {
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            const bytes = new Uint8Array(pcm16Buffer);
            const b64 = btoa(String.fromCharCode(...bytes));
            ws.send(JSON.stringify({ type: 'audio_chunk', speaker: 'clinician', data: b64 }));
          }
        }
      };

      source.connect(pcm16Node);
      micAudioCtxRef.current = audioCtx;
      micPcm16NodeRef.current = pcm16Node;
      micSourceNodeRef.current = source;
      micStreamRef.current = stream;
      micActiveRef.current = true;
    } catch (err) {
      console.error('Clinician mic capture failed:', err);
    }
  }, []);

  const stopMicCapture = useCallback(() => {
    if (micPcm16NodeRef.current) { micPcm16NodeRef.current.port.close(); micPcm16NodeRef.current = null; }
    if (micSourceNodeRef.current) { micSourceNodeRef.current.disconnect(); micSourceNodeRef.current = null; }
    if (micAudioCtxRef.current) { micAudioCtxRef.current.close(); micAudioCtxRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }
    micActiveRef.current = false;
  }, []);

  // Shared takeover toggle: flips MATILDA voice on the server AND auto-(un)mutes the
  // clinician's mic so the patient only hears the clinician once they've taken over.
  // Used by both the top-bar control button and the escalation-card button.
  const toggleTakeover = useCallback(() => {
    setMatildaVoice(prev => {
      const next = !prev;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'matilda_voice', enabled: next }));
      }
      // next=true → MATILDA back in charge → mic OFF.
      // next=false → clinician took over → mic ON.
      setAudioMuted(next);
      if (next) {
        stopMicCapture();
      } else {
        startMicCapture();
      }
      return next;
    });
  }, [startMicCapture, stopMicCapture]);

  // --- Play incoming PCM16 audio from patient ---
  // Note: incoming playback is NOT gated by audioMuted — clinician should always hear the
  // patient + MATILDA so they can monitor the consult and decide when to take over.
  // 24kHz playback context matches the server-side sample rate; no resampling needed.
  const playbackQueueRef = useRef([]);
  const playPcm16 = useCallback((base64, sampleRate = 24000) => {
    if (!playbackCtxRef.current) {
      playbackCtxRef.current = createAudioContext({ sampleRate: 24000 });
    }
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const numSamples = bytes.length / 2;
      const buffer = ctx.createBuffer(1, numSamples, sampleRate);
      const ch = buffer.getChannelData(0);
      for (let i = 0; i < bytes.length; i += 2) {
        const sample = bytes[i] | (bytes[i + 1] << 8);
        ch[i / 2] = sample > 32767 ? (sample - 65536) / 32768 : sample / 32768;
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      const now = ctx.currentTime;
      if (playbackNextTimeRef.current < now) {
        playbackNextTimeRef.current = now + 0.02;
      }
      src.start(playbackNextTimeRef.current);
      playbackNextTimeRef.current += buffer.duration;
      playbackQueueRef.current.push(src);
      src.onended = () => {
        const i = playbackQueueRef.current.indexOf(src);
        if (i >= 0) playbackQueueRef.current.splice(i, 1);
      };
    } catch (err) {
      console.error('Audio playback error:', err);
    }
  }, []);

  const flushPlayback = useCallback(() => {
    playbackQueueRef.current.forEach(src => { try { src.stop(); src.disconnect(); } catch {} });
    playbackQueueRef.current = [];
    if (playbackCtxRef.current) {
      playbackNextTimeRef.current = playbackCtxRef.current.currentTime + 0.02;
    }
  }, []);

  // Wipe all session-scoped UI state for a fresh demo. Server-side state is wiped
  // separately via the 'reset_session' WS message — this just resets the local
  // React state so what's on screen matches a clean room.
  const resetClientSession = useCallback(() => {
    flushPlayback();
    stopMicCapture();
    setPatientLeftDeliberately(false);
    // Conversation + timeline
    setTranscript([]);
    setAnalysisHistory([]);
    // Vitals + freshness
    setVitals({ bpm: 0, breathingRate: null, bmi: null, bp: null, spo2: null });
    setVitalUpdatedAt({ hr: null, rr: null, spo2: null });
    setVitalsStatus(null);
    liveStartTimeRef.current = null;
    // Frame analysis
    setEmotion({ dominant: null, age: null, gender: null });
    setDiag(null);
    overlayDataRef.current.breathing = null;
    setSpectrum({ frequencies: [], amplitudes: [] });
    setFrameDescription('');
    setIsAnalyzing(false);
    // Triage
    setTriageScore(null);
    setTriageReasoning('');
    setTriageConfidence(null);
    setTriageHistory([]);
    // Escalation
    setEscalationActive(false);
    setEscalationSteps([]);
    setEscalationDismissed(false);
    setEscalationNotice(null);
    setEscalationEta(null);
    setFlight(null);
    setPatientMic({ muted: false, by: 'patient' });
    setSessionEnded(false);
    // NOTE: patientPresent is deliberately NOT reset here — "New Session" wipes
    // clinical state, it does not disconnect the patient.
    escalatedScoreRef.current = null;
    // Identity
    setIdentityThumbnail(null);
    setIdentityStatus('enrolling');
    setIdentityConfidence(null);
    setIdentityReason(null);
    setIdentityCheckedAt(null);
    setIdentityFailures(0);
    // Insight + RDR + LLM vitals
    setInsightMessages([]);
    setRdrObservations([]);
    setLlmVitals({ gcs: null, avpu: null, painScore: null, newPain: null, sedationScore: null, temperature: null, bgl: null });
    // BGV
    setBgvState({
      initialized: false, frameCount: 0,
      structure: null, dynamics: null,
      precision: { structure: 0.5, dynamics: 0.5 },
      lastObservation: null, changesDetected: null, surpriseLevel: 'low', surpriseReason: '',
      prediction: null, predictionAccuracy: null, beliefsSummary: null
    });
    setBgvLoading(false);
    // Assessment + handover
    setAssessmentResults(null);
    setHealthRecordAnalysis(null);
    // Controls back to defaults — MATILDA-led, mic off
    setMatildaVoice(true);
    setAudioMuted(true);
  }, [flushPlayback, stopMicCapture]);

  // Load Google Identity Services and fetch client ID
  useEffect(() => {
    if (user) return;

    fetch('/api/auth/config').then(r => r.json()).then(cfg => {
      // Set this before the clientId guard below — the dev bypass has to be
      // reachable precisely when Google is unusable.
      setDevAuthAvailable(!!cfg.devAuth);
      if (!cfg.clientId || cfg.clientId === 'your_google_client_id_here') {
        console.warn('Google Client ID not configured');
        return;
      }
      setGoogleClientId(cfg.clientId);

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {
        window.google.accounts.id.initialize({
          client_id: cfg.clientId,
          callback: async (response) => {
            try {
              const res = await fetch('/api/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential })
              });
              const data = await res.json();
              if (res.ok) {
                localStorage.setItem('matilda_auth', JSON.stringify(data));
                setUser(data);
              } else {
                alert(data.error || 'Sign-in failed');
              }
            } catch (err) {
              console.error('Auth failed:', err);
            }
          }
        });
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-btn'),
          { theme: 'filled_black', size: 'large', shape: 'pill', width: 280 }
        );
      };
      document.body.appendChild(script);
    }).catch(() => {});
  }, [user]);

  const handleSignOut = () => {
    localStorage.removeItem('matilda_auth');
    setUser(null);
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  useEffect(() => {
    if (!user) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsPort = window.location.port === '3000' ? '5001' : window.location.port;
    const wsHost = `${wsProtocol}//${window.location.hostname}${wsPort ? ':' + wsPort : ''}`;
    const ws = new WebSocket(wsHost);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'join', role: 'clinician', room: roomId, token: getAuthToken() }));
    };

    ws.binaryType = 'blob';

    const nextFrameMsg = JSON.stringify({ type: 'next_frame' });
    function requestNextFrame() {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(nextFrameMsg);
      }
    }

    // Kick off pulling once connected
    const pullStarter = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        requestNextFrame();
        clearInterval(pullStarter);
      }
    }, 100);

    ws.onmessage = (event) => {
      if (event.data instanceof Blob) {
        const url = URL.createObjectURL(event.data);
        const img = videoImgRef.current;
        if (img) {
          const oldUrl = img._blobUrl;
          img.src = url;
          img._blobUrl = url;
          // Wait for the new frame to decode before revoking the previous blob URL,
          // so the browser keeps the old pixels visible until the new ones are ready.
          (img.decode ? img.decode() : Promise.resolve())
            .catch(() => {})
            .finally(() => {
              if (oldUrl && oldUrl !== url) URL.revokeObjectURL(oldUrl);
            });
        }
        pendingFrameRef.current = url;
        if (connectionStatus !== 'live') setConnectionStatus('live');
        // Server now pushes frames automatically — no need to chain a pull request.
        return;
      }

      try {
        const msg = JSON.parse(event.data);

        // Legacy JSON patient_frame support (fallback)
        if (msg.type === 'patient_frame' && msg.data) {
          if (videoImgRef.current) {
            videoImgRef.current.src = msg.data;
          }
          pendingFrameRef.current = msg.data;
          if (connectionStatus !== 'live') setConnectionStatus('live');
          return;
        }

        if (msg.type === 'auth-error') {
          localStorage.removeItem('matilda_auth');
          window.location.reload();
          return;
        }

        if (msg.type === 'peer-joined' && msg.role === 'patient') {
          setConnectionStatus('connecting');
          setPatientLeftDeliberately(false);
          setPatientPresent(true);
          // Scene processing starts itself the moment the patient is on — the
          // clinician shouldn't have to remember to switch it on before the
          // first vitals window passes.
          setAutoAnalysis(true);
          // Mic does NOT auto-start on patient join — clinician is muted until they take over.
        }

        if (msg.type === 'escalation_notice') {
          setEscalationNotice({
            clinician: msg.clinician || null,
            atsLabel: msg.atsLabel || null,
            triageScore: msg.triageScore ?? null
          });
          if (typeof msg.etaSeconds === 'number') setEscalationEta(msg.etaSeconds);
          return;
        }

        if (msg.type === 'flight_dispatched' || msg.type === 'flight_update') {
          setFlight(msg.flight || null);
          return;
        }

        if (msg.type === 'session_ended') {
          setSessionEnded(true);
          setPatientPresent(false);
          setConnectionStatus('waiting');
          stopMicCapture();
          return;
        }

        if (msg.type === 'patient_mic_state') {
          setPatientMic({ muted: !!msg.muted, by: msg.by || 'patient' });
          return;
        }

        // MATILDA was cut off mid-sentence: replace the optimistic full-text
        // entry with what the patient actually heard.
        // The on-call doctor took (or handed back) control from their phone.
        if (msg.type === 'matilda_voice_state') {
          if (msg.by !== 'clinician') {
            setMatildaVoice(msg.enabled);
            setAudioMuted(msg.enabled);
            if (msg.enabled) stopMicCapture(); // MATILDA is back; console mic off
          }
          return;
        }

        if (msg.type === 'transcript_correction') {
          const fix = t => (
            t.timestamp === msg.timestamp && t.speaker === 'matilda'
              ? { ...t, text: msg.text, interrupted: true }
              : t
          );
          setTranscript(prev => prev.map(fix));
          setAnalysisHistory(prev => prev.map(fix));
          return;
        }

        if (msg.type === 'peer-left') {
          setConnectionStatus('waiting');
          setLatestFrame(null);
          if (msg.role === 'patient') setPatientPresent(false);
          stopMicCapture();
        }

        // Patient tapped "Leave call" — clinically distinct from a dropout in
        // the field, so surface it as deliberate rather than "waiting".
        if (msg.type === 'patient_left') {
          setPatientLeftDeliberately(true);
          setConnectionStatus('waiting');
          setLatestFrame(null);
          setPatientMic({ muted: false, by: 'patient' });
          setPatientPresent(false);
          stopMicCapture();
        }

        if (msg.type === 'session_reset') {
          resetClientSession();
          return;
        }

        if (msg.type === 'matilda_speaking') {
          if (msg.flush) flushPlayback();
          return;
        }

        if (msg.type === 'audio_chunk' && (msg.speaker === 'patient' || msg.speaker === 'matilda')) {
          playPcm16(msg.data, msg.sampleRate || 24000);
          return;
        }

        if (msg.type === 'transcript') {
          setTranscript(prev => [...prev, { speaker: msg.speaker, text: msg.text, timestamp: msg.timestamp }]);
          setAnalysisHistory(prev => [...prev, {
            type: 'transcript',
            time: new Date().toLocaleTimeString(),
            speaker: msg.speaker,
            text: msg.text,
            // Carried so a later transcript_correction can find this entry.
            timestamp: msg.timestamp
          }].slice(-200));
          return;
        }

        if (msg.type === 'agent_step' && msg.step) {
          setEscalationActive(true);
          setEscalationSteps(prev => {
            const without = prev.filter(s => s.id !== msg.step.id);
            return [...without, msg.step];
          });
          return;
        }

        // Re-escalation: severity got worse, clear old steps and show fresh sequence
        if (msg.type === 'agent_reset') {
          setEscalationSteps([]);
          setEscalationActive(true);
          setEscalationDismissed(false);
          return;
        }

        // Vitals data from Python pipeline
        if (msg.image || msg.integerValue !== undefined) {
          const now = Date.now();
          setVitals(prev => ({
            bpm: msg.integerValue || prev.bpm,
            breathingRate: msg.breathingRate || prev.breathingRate,
            bmi: msg.bmi || prev.bmi,
            bp: msg.bloodPressure || prev.bp,
            spo2: msg.spo2 || prev.spo2
          }));
          setVitalUpdatedAt(prev => ({
            hr: msg.integerValue ? now : prev.hr,
            rr: msg.breathingRate ? now : prev.rr,
            spo2: msg.spo2?.value ? now : prev.spo2
          }));
          if (msg.vitalsStatus) setVitalsStatus(msg.vitalsStatus);
          if (msg.diagnostics) setDiag(msg.diagnostics);
          if (msg.spectrum) setSpectrum(msg.spectrum);
          if (msg.emotion) {
            setEmotion(msg.emotion);
          }
        }
        if (msg.overlays) {
          if (msg.overlays.pose) overlayDataRef.current.pose = msg.overlays.pose;
          if (msg.overlays.faceBbox) overlayDataRef.current.faceBbox = msg.overlays.faceBbox;
          // Assigned unconditionally: the hand leaves frame constantly, and a
          // truthy-only assignment would leave the last box painted on screen
          // after the hand is gone, implying BP is still being measured.
          overlayDataRef.current.handBbox = msg.overlays.handBbox || null;
          if (msg.overlays.frameDims) overlayDataRef.current.frameDims = msg.overlays.frameDims;
          // Into the ref, deliberately: this arrives ~4x a second and putting it
          // in state would re-render the whole console at that rate. The chest
          // trace subscribes to the ref itself.
          if (msg.overlays.breathing) overlayDataRef.current.breathing = msg.overlays.breathing;
        }

        // Patient identity verification
        if (msg.type === 'identity_enrolled') {
          setIdentityThumbnail(msg.thumbnail || null);
          setIdentityStatus('checking');
          setIdentityFailures(0);
        }
        if (msg.type === 'identity_status') {
          setIdentityStatus(msg.status || 'unknown');
          if (typeof msg.confidence === 'number') setIdentityConfidence(msg.confidence);
          if (msg.reason !== undefined) setIdentityReason(msg.reason || null);
          if (typeof msg.consecutiveFailures === 'number') setIdentityFailures(msg.consecutiveFailures);
          setIdentityAbsentSeconds(msg.status === 'absent' ? (msg.absentSeconds || 0) : 0);
          if (msg.ts) setIdentityCheckedAt(msg.ts);
          if (msg.status === 'enrolling') {
            setIdentityThumbnail(null);
            setIdentityFailures(0);
            setIdentityConfidence(null);
            setIdentityReason(null);
          }
        }
      } catch (e) {}
    };

    ws.onclose = () => setConnectionStatus('disconnected');

    return () => {
      clearInterval(pullStarter);
      ws.close();
      if (frameRafRef.current) cancelAnimationFrame(frameRafRef.current);
      stopMicCapture();
      if (playbackCtxRef.current) { playbackCtxRef.current.close(); playbackCtxRef.current = null; }
    };
  }, [roomId, user, startMicCapture, stopMicCapture, playPcm16]);

  // Overlay rendering loop — draws pose + HR box on a canvas over the video
  const drawOverlays = useCallback(() => {
    const canvas = canvasRef.current;
    const img = videoImgRef.current;
    if (!canvas || !img || !img.naturalWidth || !img.naturalHeight) {
      overlayRafRef.current = requestAnimationFrame(drawOverlays);
      return;
    }
    // The <img> uses object-fit: contain, so the element box is the container
    // but the actual rendered image is letterboxed inside it. Compute the real
    // content rect from naturalWidth/Height and overlay the canvas exactly on top.
    const imgRect = img.getBoundingClientRect();
    const parentRect = canvas.parentElement.getBoundingClientRect();
    const boxW = imgRect.width;
    const boxH = imgRect.height;
    const naturalRatio = img.naturalWidth / img.naturalHeight;
    const boxRatio = boxW / boxH;
    let w, h;
    if (boxRatio > naturalRatio) {
      // Box is wider than the image — letterbox left/right
      h = boxH;
      w = h * naturalRatio;
    } else {
      // Box is taller than the image — letterbox top/bottom
      w = boxW;
      h = w / naturalRatio;
    }
    const offX = (imgRect.left - parentRect.left) + (boxW - w) / 2;
    const offY = (imgRect.top - parentRect.top) + (boxH - h) / 2;
    canvas.width = w;
    canvas.height = h;
    canvas.style.left = offX + 'px';
    canvas.style.top = offY + 'px';
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);

    const { pose, faceBbox, handBbox } = overlayDataRef.current;

    if (showPose && pose && pose.length > 0) {
      // Draw connections
      ctx.strokeStyle = 'rgba(0, 255, 100, 0.7)';
      ctx.lineWidth = 2;
      for (const [a, b] of POSE_CONNECTIONS) {
        const pa = pose[a];
        const pb = pose[b];
        if (pa && pb && pa.v > 0.5 && pb.v > 0.5) {
          ctx.beginPath();
          ctx.moveTo(pa.x * w, pa.y * h);
          ctx.lineTo(pb.x * w, pb.y * h);
          ctx.stroke();
        }
      }
      // Draw key landmarks
      ctx.fillStyle = 'rgba(0, 220, 255, 0.85)';
      for (const lm of pose) {
        if (lm.v > 0.5) {
          ctx.beginPath();
          ctx.arc(lm.x * w, lm.y * h, 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      // Breathing indicator — shoulder midpoint
      const ls = pose[11];
      const rs = pose[12];
      if (ls && rs && ls.v > 0.5 && rs.v > 0.5) {
        const mx = ((ls.x + rs.x) / 2) * w;
        const my = ((ls.y + rs.y) / 2) * h;
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(mx, my, 8, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = 'rgba(56, 189, 248, 0.3)';
        ctx.fill();
        ctx.font = '10px sans-serif';
        ctx.fillStyle = 'rgba(56, 189, 248, 0.9)';
        ctx.fillText('RESP', mx + 12, my + 4);
      }
    }

    if (showHrBox && faceBbox) {
      const bx = faceBbox.x * w;
      const by = faceBbox.y * h;
      const bw = faceBbox.w * w;
      const bh = faceBbox.h * h;

      ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(bx, by, bw, bh);
      ctx.setLineDash([]);
      // No text label: the 'HR ROI' toggle chip at top-left already names
      // this box, and the duplicate label was adding HUD noise.
    }

    // Hand ROI — the distal PPG site for the PTT that drives BP. Drawn
    // whenever the hand is detected (not gated on the HR-box setting), since
    // its whole purpose is telling the clinician the hand is positioned well
    // enough for a BP reading.
    if (handBbox) {
      const hx = handBbox.x * w;
      const hy = handBbox.y * h;
      const hw = handBbox.w * w;
      const hh = handBbox.h * h;
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.85)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(hx, hy, hw, hh);
      ctx.setLineDash([]);
      ctx.font = 'bold 10px sans-serif';
      ctx.fillStyle = 'rgba(245, 158, 11, 0.95)';
      ctx.fillText('BP ROI (hand)', hx + 4, hy - 6);
    }

    overlayRafRef.current = requestAnimationFrame(drawOverlays);
  }, [showPose, showHrBox]);

  useEffect(() => {
    overlayRafRef.current = requestAnimationFrame(drawOverlays);
    return () => { if (overlayRafRef.current) cancelAnimationFrame(overlayRafRef.current); };
  }, [drawOverlays]);

  // Force canvas re-sync when video container resizes
  useEffect(() => {
    const img = videoImgRef.current;
    if (!img) return;
    const container = img.parentElement;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (overlayRafRef.current) cancelAnimationFrame(overlayRafRef.current);
      overlayRafRef.current = requestAnimationFrame(drawOverlays);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [drawOverlays]);

  // GPT Vision analysis — grab current frame from the img element via canvas
  const analyzeCurrentFrame = useCallback(async () => {
    const img = videoImgRef.current;
    if (!img || !img.naturalWidth) return;
    setIsAnalyzing(true);

    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    c.getContext('2d').drawImage(img, 0, 0);
    const base64 = c.toDataURL('image/jpeg', 0.7).split(',')[1];

    // Capture thumbnail now (same frame sent to GPT-4o)
    let thumb = null;
    try {
      const tc = document.createElement('canvas');
      tc.width = 120; tc.height = 90;
      tc.getContext('2d').drawImage(img, 0, 0, 120, 90);
      thumb = tc.toDataURL('image/jpeg', 0.5);
    } catch {}

    try {
      const response = await fetch('/api/analyze-frame', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ image: base64 })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            try {
              const parsed = JSON.parse(data);
              const token = parsed.content || parsed.choices?.[0]?.delta?.content || '';
              if (token) {
                fullText += token;
                setFrameDescription(fullText);
              }
            } catch {}
          }
        }
      }

      // Meta OODA Loop — unified triage + ABCDE + reasoning
      const oodaResp = await fetch('/api/ooda-loop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          sceneDescription: fullText,
          vitals: clinicalVitals,
          emotion,
          previousObservations: analysisHistory,
          healthRecord,
          roomId,
          transcript: transcript.slice(-20).map(t => `[${t.speaker}]: ${t.text}`).join('\n')
        })
      });
      const ooda = await oodaResp.json();

      // Push live clinical context to MATILDA so she can respond intelligently
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'context_update',
          vitals: clinicalVitals,
          emotion,
          triage: ooda.triage?.score || null,
          observation: fullText,
          healthRecord
        }));
      }

      if (ooda.triage?.score) {
        setTriageScore(ooda.triage.score);
        setTriageReasoning(ooda.triage.reasoning || '');
        setTriageConfidence(ooda.triage.confidence || null);
        setTriageHistory(prev => [...prev, {
          score: ooda.triage.score,
          reasoning: ooda.triage.reasoning || '',
          time: new Date().toLocaleTimeString()
        }].slice(-10));
      }

      if (ooda.abcde) {
        setAssessmentResults(prev => ({
          ...prev,
          stage: {
            abcde_findings: ooda.abcde,
            overall_completeness_percentage: ooda.abcde.overall_completeness_percentage
          }
        }));
        const d = ooda.abcde.disability;
        if (d) {
          setLlmVitals({
            gcs: d.gcs || null,
            avpu: d.avpu || null,
            painScore: d.pain_score != null ? d.pain_score : null,
            newPain: d.new_pain || null,
            sedationScore: d.sedation_score != null ? d.sedation_score : null,
            temperature: d.temperature || null,
            bgl: d.blood_glucose || null
          });
          llmVitalsTimestamp.current = Date.now();
        }
      }

      setAnalysisHistory(prev => [...prev, {
        time: new Date().toLocaleTimeString(),
        description: fullText,
        triage: ooda.triage?.score,
        thumbnail: thumb
      }].slice(-200));

      // Auto-post OODA reasoning into Insight chat (structured for collapse)
      if (ooda.observe) {
        setInsightMessages(prev => [...prev, {
          role: 'assistant',
          content: ooda.act || '',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isOoda: true,
          knowledgeBank: ooda.knowledgeBank || null,
          reasoning: {
            observe: ooda.observe,
            orient: ooda.orient,
            decide: ooda.decide
          }
        }]);
        // Insight panel stays in its current state — user controls it

        // Push the latest reasoning to the server too. The escalation handover
        // brief is assembled server-side, and until now only had vitals — the
        // Observe/Orient text lived solely in this component's state, so the
        // receiving clinician never saw why the escalation happened.
        const wsOoda = wsRef.current;
        if (wsOoda && wsOoda.readyState === WebSocket.OPEN) {
          wsOoda.send(JSON.stringify({
            type: 'context_update',
            ooda: {
              observe: ooda.observe || null,
              orient: ooda.orient || null,
              at: new Date().toISOString()
            },
            // Per-domain ABCDE completeness. The server watches this for a
            // stalled assessment and brings a doctor in if it never comes
            // together — a check that cannot live here, because it has to keep
            // working when this console is closed mid-consult.
            abcde: ooda.abcde || null,
            oncallPhone: oncallPhoneRef.current || null
          }));
        }
      }

      // Belief-Guided Perception — uses existing description, no extra image call
      if (bgvEnabled && fullText) {
        setBgvLoading(true);
        try {
          const bgvResp = await fetch('/api/belief-guided-vision', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              sceneDescription: fullText,
              vitals: clinicalVitals,
              beliefState: bgvState.initialized ? {
                initialized: bgvState.initialized,
                frame_count: bgvState.frameCount,
                structure: bgvState.structure,
                dynamics: bgvState.dynamics,
                precision: bgvState.precision,
                prediction: bgvState.prediction
              } : null
            })
          });
          const bgvResult = await bgvResp.json();
          if (bgvResult.success) {
            setBgvState({
              initialized: true,
              frameCount: bgvResult.frame_count || (bgvState.frameCount + 1),
              structure: bgvResult.structure || bgvState.structure,
              dynamics: bgvResult.dynamics || bgvState.dynamics,
              precision: bgvResult.precision || bgvState.precision,
              lastObservation: bgvResult.observation,
              changesDetected: bgvResult.changes_detected || null,
              surpriseLevel: bgvResult.changes_detected?.surprise_level || 'low',
              surpriseReason: bgvResult.changes_detected?.surprise_reason || '',
              prediction: bgvResult.prediction || null,
              predictionAccuracy: bgvResult.prediction_accuracy || null,
              beliefsSummary: bgvResult.beliefs_summary || null
            });

            const bgvTime = new Date().toLocaleTimeString();
            const bgvEntries = [];
            if (bgvResult.prediction_accuracy) {
              bgvEntries.push({ time: bgvTime, type: 'bgv-accuracy', content: bgvResult.prediction_accuracy });
            }
            const cd = bgvResult.changes_detected;
            if (bgvResult.observation || (cd && (cd.medical_concerns || cd.dynamics_changed || cd.structure_changed))) {
              bgvEntries.push({
                time: bgvTime, type: 'bgv-changes',
                content: bgvResult.observation || '',
                alerts: cd || null,
                surpriseLevel: cd?.surprise_level || 'low',
                surpriseReason: cd?.surprise_reason || ''
              });
            }
            if (bgvResult.structure || bgvResult.dynamics || bgvResult.beliefs_summary) {
              bgvEntries.push({
                time: bgvTime, type: 'bgv-beliefs',
                summary: bgvResult.beliefs_summary || null,
                structure: bgvResult.structure || null,
                dynamics: bgvResult.dynamics || null
              });
            }
            if (bgvResult.prediction) {
              bgvEntries.push({ time: bgvTime, type: 'bgv-prediction', content: bgvResult.prediction });
            }
            if (bgvEntries.length > 0) {
              setAnalysisHistory(prev => [...prev, ...bgvEntries].slice(-200));
            }

            // Push BGV surprise + beliefs into MATILDA's live context so she can react to changes
            const ws2 = wsRef.current;
            if (ws2 && ws2.readyState === WebSocket.OPEN) {
              ws2.send(JSON.stringify({
                type: 'context_update',
                surprise: {
                  level: bgvResult.changes_detected?.surprise_level || 'low',
                  reason: bgvResult.changes_detected?.surprise_reason || '',
                  medicalConcerns: bgvResult.changes_detected?.medical_concerns || false
                },
                beliefsSummary: bgvResult.beliefs_summary || null,
                prediction: bgvResult.prediction || null
              }));
            }
          }
        } catch (bgvErr) {
          console.error('BGV error:', bgvErr);
        }
        setBgvLoading(false);
      }

    } catch (err) {
      console.error('Analysis error:', err);
    }
    setIsAnalyzing(false);
  }, [analysisHistory, clinicalVitals, emotion, healthRecord, bgvEnabled, bgvState]);

  // Auto-analysis — use refs to avoid interval reset on every state change
  const analyzeRef = useRef(analyzeCurrentFrame);
  const isAnalyzingRef = useRef(isAnalyzing);
  useEffect(() => { analyzeRef.current = analyzeCurrentFrame; }, [analyzeCurrentFrame]);
  useEffect(() => { isAnalyzingRef.current = isAnalyzing; }, [isAnalyzing]);

  useEffect(() => {
    if (!autoAnalysis || connectionStatus !== 'live') return;
    // 8s was right when a pass was one LLM call. It is now two, with an
    // embedding and a vector search between them — measured at 9-13s end to
    // end. The isAnalyzing guard already prevents overlap, so an 8s timer just
    // means most ticks are discarded; the real cadence was never 8s once the
    // pass got slower. Stating it honestly beats a timer that lies.
    const interval = setInterval(() => {
      if (!isAnalyzingRef.current) analyzeRef.current();
    }, Number(process.env.REACT_APP_ANALYSIS_INTERVAL_MS) || 12000);
    return () => clearInterval(interval);
  }, [autoAnalysis, connectionStatus]);

  // Record when the patient went live (used for the "Calibrating…" state) and tick
  // every 5s so the "Xs ago" age labels next to each vital stay current.
  useEffect(() => {
    if (connectionStatus === 'live' && !liveStartTimeRef.current) {
      liveStartTimeRef.current = Date.now();
    } else if (connectionStatus !== 'live') {
      liveStartTimeRef.current = null;
      setVitalUpdatedAt({ hr: null, rr: null, spo2: null });
    }
  }, [connectionStatus]);
  useEffect(() => {
    const t = setInterval(() => setVitalsTick(n => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // RDR — record vitals snapshot periodically
  const vitalsRef = useRef(clinicalVitals);
  const emotionRef = useRef(emotion);
  const triageRef = useRef(triageScore);
  const llmVitalsRef = useRef(llmVitals);
  useEffect(() => { vitalsRef.current = clinicalVitals; }, [clinicalVitals]);
  useEffect(() => { emotionRef.current = emotion; }, [emotion]);
  useEffect(() => { triageRef.current = triageScore; }, [triageScore]);
  // Read-only mirror for the escalation trigger: including assessmentResults in
  // that effect's deps would re-fire escalation on every reassessment.
  const oncallPhoneRef = useRef(oncallPhone);
  useEffect(() => { oncallPhoneRef.current = oncallPhone; }, [oncallPhone]);

  // Push the number the moment it changes, and again on connect.
  //
  // It otherwise only travelled inside the analysis loop's context_update, so
  // with auto-analysis off — or simply before the first pass — the server held
  // no number, and a stall-triggered escalation would page the env default
  // instead of whoever is actually on call tonight.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({
      type: 'context_update',
      oncallPhone: oncallPhone || null,
      medchestPhone: medchestPhone || null
    }));
  }, [oncallPhone, medchestPhone, connectionStatus]);
  const assessmentResultsRef = useRef(assessmentResults);
  useEffect(() => { assessmentResultsRef.current = assessmentResults; }, [assessmentResults]);
  const llmVitalsSnapshotRef = useRef(llmVitals);
  useEffect(() => { llmVitalsSnapshotRef.current = llmVitals; }, [llmVitals]);

  // Manual page, for when the doctor needs the link again (new phone, expired
  // link, or an escalation that has not fired yet).
  const sendOncallLink = useCallback(async () => {
    if (oncallSending) return;
    setOncallSending(true);
    setOncallResult(null);
    try {
      const res = await fetch('/api/oncall-link/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ phone: oncallPhone, room: roomId, triageScore })
      });
      const data = await res.json();
      setOncallResult(res.ok && data.ok
        ? { ok: true, message: `Link sent to ${data.to}` }
        : { ok: false, message: data.smsError || data.error || 'Could not send the link' });
    } catch (err) {
      setOncallResult({ ok: false, message: err.message });
    } finally {
      setOncallSending(false);
    }
  }, [oncallPhone, oncallSending, roomId, triageScore]);

  // The ISBAR route sits behind requireAuth, so a plain link would 401. Fetch it
  // with the bearer token and hand the blob to the browser.
  const downloadEscalationIsbar = useCallback(async (url, filename) => {
    try {
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Handover download failed (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'RFDS_ISBAR_Handover.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('ISBAR download error:', err);
    }
  }, []);

  // Auto-escalate only when triage hits ATS 1 or 2, and re-escalate when severity increases (e.g. 2 → 1)
  useEffect(() => {
    if (!triageScore || triageScore > 2) return;
    // Only trigger if we've never escalated, or current score is MORE severe than the previous escalation
    if (escalatedScoreRef.current !== null && triageScore >= escalatedScoreRef.current) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    escalatedScoreRef.current = triageScore;
    setEscalationDismissed(false);
    // Everything the server cannot see for itself. Vitals, health record and
    // observation already live in the room's clinical context; ABCDE and the
    // LLM-reported vitals only exist here, and the escalation handover is an
    // ISBAR document that wants them.
    const abcde = assessmentResultsRef.current?.stage?.abcde_findings || null;
    const lv = llmVitalsSnapshotRef.current || {};
    wsRef.current.send(JSON.stringify({
      type: 'escalation_trigger',
      triageScore,
      reasoning: triageReasoning || '',
      oncallPhone: oncallPhoneRef.current || null,
      snapshot: {
        abcde: abcde ? {
          airway: abcde.airway?.status,
          breathing: abcde.breathing?.status,
          circulation: abcde.circulation?.status,
          disability: abcde.disability?.status,
          exposure: abcde.exposure?.status
        } : null,
        assessmentStage: assessmentResultsRef.current?.stage?.stage || null,
        temperature: lv.temperature || null
      }
    }));
  }, [triageScore, triageReasoning]);

  // Mirror of the patient's ETA countdown, for the video overlay.
  const escEtaCountingDown = escalationEta != null && escalationEta > 0;
  useEffect(() => {
    if (!escEtaCountingDown) return;
    const timer = setInterval(() => {
      setEscalationEta(prev => (prev == null ? null : Math.max(0, prev - 1)));
    }, 1000);
    return () => clearInterval(timer);
  }, [escEtaCountingDown]);

  // Ask the patient's client to mute/unmute their mic. A request, not a forced
  // action — the patient is told, and can undo it. We do not optimistically
  // flip our own state: the button updates when the patient's client confirms.
  const setPatientMicMuted = useCallback((muted) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'set_patient_mic', muted, room: roomId }));
  }, [roomId]);

  // End the consult for both parties. The patient is told and their capture
  // released; this console keeps the transcript, vitals and assessment so the
  // clinician can finish documenting. Only 'New Session' clears that.
  const endCall = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'end_session', room: roomId }));
    }
    stopMicCapture();
    setAudioMuted(true);
    setAutoAnalysis(false);
    setConfirmEndCall(false);
    setSessionEnded(true);
  }, [roomId, stopMicCapture]);

  // Task an aeromedical retrieval. The server owns the simulation so both the
  // console and the patient's phone see the same aircraft and the same ETA.
  const sendPlane = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'send_plane', room: roomId }));
  }, [roomId]);

  // Mint a single-use join link WITHOUT texting it, for handing over by another
  // channel. Same endpoint and token lifecycle as the SMS path, so invite
  // creation stays in one place.
  const createInviteLink = useCallback(async () => {
    if (inviteBusy) return;
    setInviteBusy(true);
    setInviteMsg(null);
    try {
      const resp = await fetch('/api/patient-link/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ room: roomId, deliver: false })
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.link) {
        setInviteLink(data.link);
        setInviteMsg({ ok: true, text: `Valid ${data.expiresInMinutes} min · single use` });
        try {
          await navigator.clipboard.writeText(data.link);
          setInviteMsg({ ok: true, text: `Copied · valid ${data.expiresInMinutes} min` });
        } catch {
          // Clipboard needs a secure context / permission; the link is shown
          // regardless so it can still be selected by hand.
        }
      } else {
        setInviteMsg({ ok: false, text: data.error || 'Could not create a link.' });
      }
    } catch {
      setInviteMsg({ ok: false, text: 'Network error creating the link.' });
    } finally {
      setInviteBusy(false);
    }
  }, [roomId, inviteBusy]);

  // Text the patient a single-use join link instead of reading out a password.
  const sendPatientLink = useCallback(async () => {
    const phone = smsPhone.trim();
    if (!phone || smsSending) return;
    setSmsSending(true);
    setSmsResult(null);
    try {
      const resp = await fetch('/api/patient-link/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ phone, room: roomId })
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        // Three outcomes: sent, sent-but-Twilio-errored (dev), or minted with
        // no SMS configured (dev). The link is echoed in dev either way.
        const sentLine = data.smsError
          ? `SMS failed: ${data.smsError}`
          : data.sent
            ? `Sent to ${data.to} — expires in ${data.expiresInMinutes} min.`
            : 'No SMS configured — link only.';
        setSmsResult({
          ok: !data.smsError,
          message: data.link ? `${sentLine}\n${data.link}` : sentLine
        });
        // Surface it in the Session panel too, so the link lives where people look.
        if (data.link) setInviteLink(data.link);
        setSmsPhone('');
      } else {
        setSmsResult({ ok: false, message: data.error || 'Could not send the link.' });
      }
    } catch (err) {
      setSmsResult({ ok: false, message: 'Network error sending the link.' });
    } finally {
      setSmsSending(false);
    }
  }, [smsPhone, smsSending, roomId]);
  useEffect(() => { llmVitalsRef.current = llmVitals; }, [llmVitals]);

  useEffect(() => {
    if (connectionStatus !== 'live') return;
    const record = () => {
      const v = vitalsRef.current;
      const hasData = v.bpm != null || v.breathingRate != null || v.spo2?.value != null;
      if (!hasData) return;

      const hr = v.bpm || null;
      const rr = v.breathingRate || null;
      const spo2 = v.spo2?.value || null;
      const sbp = v.bp?.systolic || null;

      const prev = lastRdrRef.current;
      if (prev && prev.hr === hr && prev.rr === rr && prev.spo2 === spo2 && prev.sbp === sbp) return;

      const lv = llmVitalsRef.current;
      const now = new Date();
      const llmFresh = llmVitalsTimestamp.current && (Date.now() - llmVitalsTimestamp.current < 35000);
      const snap = {
        time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        date: now.toLocaleDateString([], { day: '2-digit', month: '2-digit' }),
        hr, rr, spo2, sbp,
        dbp: v.bp?.diastolic || null,
        gcs: llmFresh ? lv.gcs : null,
        avpu: llmFresh ? lv.avpu : null,
        painScore: llmFresh ? lv.painScore : null,
        newPain: llmFresh ? lv.newPain : null,
        sedationScore: llmFresh ? lv.sedationScore : null,
        temp: llmFresh ? lv.temperature : null,
        bgl: llmFresh ? lv.bgl : null,
        triage: triageRef.current || null,
        llmDerived: llmFresh
      };
      lastRdrRef.current = snap;
      setRdrObservations(prev => [...prev, snap].slice(-30));
    };
    const initialDelay = setTimeout(record, 5000);
    const id = setInterval(record, 30000);
    return () => { clearTimeout(initialDelay); clearInterval(id); };
  }, [connectionStatus]);

  // Retrieve the record from the My Health Record FHIR Gateway. Each gateway
  // interaction reports as it completes, so the panels fill in as data lands.
  const loadHealthRecord = useCallback(async (accessType = mhrAccessType) => {
    mhrAbortRef.current?.abort();
    const controller = new AbortController();
    mhrAbortRef.current = controller;

    setIsLoadingRecord(true);
    setMhrError(null);
    setMhrEvents(RETRIEVAL_STEPS.map(s => ({ ...s, status: 'queued' })));

    try {
      await runMhrRetrieval({
        ihi: DEMO_IHI,
        accessType,
        authHeaders: authHeaders(),
        signal: controller.signal,
        onEvent: event => setMhrEvents(prev => prev.map(e => (e.id === event.id ? { ...e, ...event } : e))),
        onRecord: partial => {
          setMhrSource(partial.source);
          setMhrExtra(partial.mhr);
          // Hold the health record panel at "loading" until demographics land,
          // so it never shows an empty shell as an active record.
          if (partial.patient) setHealthRecord(partial);
        }
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('MHR retrieval error:', err);
        setMhrError(err.message || 'Retrieval failed');
        // Fall back to the local scenario record so the console stays usable.
        try {
          const res = await fetch('/api/health-record/get_record?scenario=1', { headers: authHeaders() });
          const json = await res.json();
          if (json.success && json.data) setHealthRecord(json.data);
        } catch (fallbackErr) { console.error('Health record fallback error:', fallbackErr); }
      }
    } finally {
      if (mhrAbortRef.current === controller) mhrAbortRef.current = null;
      setIsLoadingRecord(false);
    }
  }, [mhrAccessType]);

  // Auto-retrieve once the patient connects
  const healthRecordAttempted = useRef(false);
  useEffect(() => {
    if (connectionStatus === 'live' && !healthRecord && !isLoadingRecord && !healthRecordAttempted.current) {
      healthRecordAttempted.current = true;
      loadHealthRecord();
    }
  }, [connectionStatus, healthRecord, isLoadingRecord, loadHealthRecord]);

  useEffect(() => () => mhrAbortRef.current?.abort(), []);

  // Local EHR pull — independent of the gateway, so an MHR outage does not
  // also hide what the service already holds on this patient.
  useEffect(() => {
    if (connectionStatus !== 'live' || ehrRecord) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/health-record/get_record?scenario=1', { headers: authHeaders() });
        const json = await res.json();
        if (!cancelled && json.success && json.data) setEhrRecord(json.data);
      } catch (err) {
        console.error('EHR fetch error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [connectionStatus, ehrRecord]);

  // Record the declaration first, then retrieve. If the audit record cannot be
  // written we do NOT proceed: an unlogged override of a patient's access
  // controls is exactly what must not happen.
  const confirmEmergencyAccess = useCallback(async () => {
    setEmergencyRecording(true);
    try {
      const res = await fetch('/api/mhr/emergency-access-declaration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ room: roomId, ihi: DEMO_IHI, declaration: 'accepted' })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMhrError(data.error || 'Could not record the emergency access declaration — access not attempted.');
        return;
      }
      setEmergencyDialogOpen(false);
      setMhrAccessType('EmergencyAccess');
      loadHealthRecord('EmergencyAccess');
    } catch (err) {
      setMhrError(`Could not record the emergency access declaration: ${err.message}`);
    } finally {
      setEmergencyRecording(false);
    }
  }, [roomId, loadHealthRecord]);


  const analyzeHealthRecord = useCallback(async () => {
    if (!healthRecord) return;
    setIsLoadingRecord(true);
    try {
      const res = await fetch('/api/process-health-record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ healthRecord })
      });
      const json = await res.json();
      if (json.success) setHealthRecordAnalysis(json.analysis);
    } catch (err) { console.error('Health record analysis error:', err); }
    setIsLoadingRecord(false);
  }, [healthRecord]);

  // Chained clinical assessment
  const runFullAssessment = useCallback(async () => {
    if (!frameDescription) return;
    setIsAssessing(true);
    const results = {};
    const hdrs = { 'Content-Type': 'application/json', ...authHeaders() };

    try {
      setAssessmentStep('Assessment stage...');
      const stageRes = await fetch('/api/assessment-stage', {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ description: frameDescription, historyCount: analysisHistory.length })
      });
      results.stage = await stageRes.json();

      setAssessmentStep('Structured assessment...');
      const structRes = await fetch('/api/structured-assessment', {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ description: frameDescription, triageScore, assessmentStage: results.stage })
      });
      results.structured = await structRes.json();

      setAssessmentStep('Diagnosis prediction...');
      const diagRes = await fetch('/api/diagnosis-prediction', {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ description: frameDescription, triageScore, assessmentStage: results.stage })
      });
      results.diagnosis = await diagRes.json();

      setAssessmentStep('Medication recommendations...');
      const medRes = await fetch('/api/medication-recommendations', {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ diagnosisPrediction: results.diagnosis, structuredAssessment: results.structured, triageScore })
      });
      results.medications = await medRes.json();

      setAssessmentResults(results);
    } catch (err) { console.error('Assessment error:', err); }
    setIsAssessing(false);
    setAssessmentStep('');
  }, [frameDescription, triageScore, analysisHistory.length]);

  const sendInsightMessage = useCallback(async () => {
    const text = insightInput.trim();
    if (!text || insightLoading) return;
    const userMsg = { role: 'user', content: text, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setInsightMessages(prev => [...prev, userMsg]);
    setInsightInput('');
    setInsightLoading(true);

    const contextParts = [];
    if (clinicalVitals.bpm) contextParts.push(`Heart Rate: ${clinicalVitals.bpm} bpm`);
    if (clinicalVitals.breathingRate) contextParts.push(`Resp Rate: ${clinicalVitals.breathingRate} br/min`);
    if (clinicalVitals.spo2?.value) contextParts.push(`SpO2: ${clinicalVitals.spo2.value}%`);
    // Guard on the reading, not the object — the BP payload is present from the
    // moment BP is enabled, so `if (bp)` alone would feed MATILDA "BP: null/null".
    if (clinicalVitals.bp?.systolic && clinicalVitals.bp?.diastolic) {
      contextParts.push(`BP: ${clinicalVitals.bp.systolic}/${clinicalVitals.bp.diastolic} mmHg`);
    }
    if (clinicalVitals.bmi) contextParts.push(`BMI: ${clinicalVitals.bmi}`);
    if (emotion.dominant) contextParts.push(`Affect: ${emotion.dominant}`);
    if (emotion.age) contextParts.push(`Est. Age: ${emotion.age}`);
    if (triageScore) contextParts.push(`Triage: ATS ${triageScore} (${ATS_LABELS_MAP[triageScore]})`);
    if (frameDescription) contextParts.push(`Latest clinical observation: ${frameDescription.slice(0, 500)}`);
    if (healthRecord?.patient) {
      const pname = `${healthRecord.patient.name?.given || ''} ${healthRecord.patient.name?.family || ''}`.trim() || 'Unknown';
      contextParts.push(`Patient: ${pname}, DOB: ${healthRecord.patient.birthDate || 'unknown'}`);
    }

    const apiMessages = [...insightMessages, userMsg].map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch('/api/insight-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ messages: apiMessages, patientContext: contextParts.join('\n') || null })
      });
      const data = await res.json();
      const stamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      // A failure must not arrive dressed as a clinical answer. Same stream,
      // different kind, rendered differently.
      if (!res.ok || (!data.reply && data.error)) {
        setInsightMessages(prev => [...prev, {
          role: 'assistant', isError: true, time: stamp,
          content: data.error || `Insight is unavailable (${res.status})`
        }]);
      } else {
        setInsightMessages(prev => [...prev, {
          role: 'assistant', content: data.reply || 'No response', time: stamp
        }]);
      }
    } catch (err) {
      setInsightMessages(prev => [...prev, {
        role: 'assistant', isError: true,
        content: `Could not reach Insight — ${err.message}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    }
    setInsightLoading(false);
  }, [insightInput, insightLoading, insightMessages, clinicalVitals, emotion, triageScore, frameDescription, healthRecord]);

  useEffect(() => {
    if (insightEndRef.current) insightEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [insightMessages, insightLoading]);

  useEffect(() => {
    if (insightOpen && insightInputRef.current) {
      setTimeout(() => insightInputRef.current?.focus(), 400);
    }
  }, [insightOpen]);

  const ATS_LABELS_MAP = { 1: 'Immediate', 2: 'Emergency', 3: 'Urgent', 4: 'Semi-urgent', 5: 'Non-urgent' };
  const ATS_LABELS = { 1: 'Immediate', 2: 'Emergency', 3: 'Urgent', 4: 'Semi-urgent', 5: 'Non-urgent' };
  const ATS_COLORS = { 1: '#dc2626', 2: '#ea580c', 3: '#eab308', 4: '#22c55e', 5: '#3b82f6' };

  const triageColor = triageScore
    ? (ATS_COLORS[triageScore] || '#6c757d')
    : '#6c757d';

  const BG = '#151521';
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: '0 0 6px' }}>M.A.T.I.L.D.A Console</h1>
        <p style={{ color: '#888', fontSize: '14px', marginBottom: '32px' }}>Sign in to access the clinician dashboard</p>
        <div id="google-signin-btn" style={{ minHeight: '44px' }} />
        {!googleClientId && (
          <p style={{ color: '#666', fontSize: '12px', marginTop: '24px' }}>
            Google Sign-In not configured. Set GOOGLE_CLIENT_ID in .env
          </p>
        )}
        {/* Local development only. The server decides whether to offer this
            (ALLOW_DEV_AUTH, and not running on Azure) — the flag arrives from
            /api/auth/config and cannot be set client-side. Google blocks its
            OAuth flow on http://localhost unless the origin is registered. */}
        {devAuthAvailable && (
          <div style={{ marginTop: '22px', textAlign: 'center' }}>
            <button
              onClick={async () => {
                try {
                  const res = await fetch('/api/auth/dev', { method: 'POST' });
                  const data = await res.json();
                  if (res.ok) {
                    localStorage.setItem('matilda_auth', JSON.stringify(data));
                    setUser(data);
                  } else {
                    alert(data.error || 'Dev sign-in unavailable');
                  }
                } catch (err) {
                  console.error('Dev sign-in failed:', err);
                }
              }}
              style={{
                padding: '10px 20px', borderRadius: '999px', cursor: 'pointer',
                background: 'rgba(234,179,8,0.14)', border: '1px solid rgba(234,179,8,0.5)',
                color: '#facc15', fontSize: '13px', fontWeight: '600'
              }}
            >Continue without Google (local dev)</button>
            <p style={{ color: '#8a7b3a', fontSize: '11px', marginTop: '9px', maxWidth: '300px', lineHeight: '1.5' }}>
              Auth bypass is active on this server. Never expose it beyond localhost.
            </p>
          </div>
        )}
        <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '14px', width: 'auto', marginTop: '48px' }} />
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh', background: BG, color: '#e4e4e7',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      {/* Header */}
      <header style={{
        display: 'grid', gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center', padding: '10px 24px', background: BG,
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0
      }}>
        {/* Left: RFDS logo + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '32px', width: 'auto' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
          <div style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '10px',
            background: connectionStatus === 'live' ? 'rgba(40,167,69,0.12)' : 'rgba(108,117,125,0.12)'
          }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%',
              background: connectionStatus === 'live' ? '#28a745' : '#6c757d' }} />
            <span style={{ fontSize: '11px', color: connectionStatus === 'live' ? '#28a745' : '#6c757d' }}>
              {connectionStatus === 'live' ? 'Live' : patientLeftDeliberately ? 'Patient left' : connectionStatus === 'waiting' ? 'Waiting...' : 'Offline'}
            </span>
          </div>
        </div>

        {/* Center: M.A.T.I.L.D.A Console */}
        <h1 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#fff', letterSpacing: '1px', textAlign: 'center' }}>
          M.A.T.I.L.D.A Console
        </h1>

        {/* Right: avatar dropdown + UNCAPT */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '12px', width: 'auto', opacity: 0.5 }} />
          <button onClick={() => setShowUserMenu(m => !m)} style={{
            width: '32px', height: '32px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.1)',
            background: 'transparent', cursor: 'pointer', padding: 0, overflow: 'hidden'
          }}>
            {user.picture ? (
              <img src={user.picture} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} referrerPolicy="no-referrer" />
            ) : (
              <span style={{ color: '#888', fontSize: '14px' }}>{(user.name || '?')[0]}</span>
            )}
          </button>
          {showUserMenu && (
            <div style={{
              position: 'absolute', top: '38px', right: 0, background: '#1e1e2d',
              borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
              padding: '8px 0', minWidth: '160px', zIndex: 100,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)'
            }}>
              <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: '12px', color: '#fff', fontWeight: '500' }}>{user.name}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{user.email || ''}</div>
              </div>
              <button onClick={() => {
                setShowUserMenu(false);
                if (!window.confirm('Start a new session? This wipes all transcripts, vitals, escalation, and identity for the current room.')) return;
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                  wsRef.current.send(JSON.stringify({ type: 'reset_session' }));
                }
                resetClientSession();
              }} style={{
                width: '100%', padding: '8px 14px', background: 'transparent', border: 'none',
                color: '#e5e7eb', fontSize: '12px', cursor: 'pointer', textAlign: 'left',
                borderBottom: '1px solid rgba(255,255,255,0.06)'
              }}>New Session</button>
              <button onClick={() => { setShowUserMenu(false); handleSignOut(); }} style={{
                width: '100%', padding: '8px 14px', background: 'transparent', border: 'none',
                color: '#dc3545', fontSize: '12px', cursor: 'pointer', textAlign: 'left'
              }}>Sign Out</button>
            </div>
          )}
        </div>
      </header>

      <audio ref={audioRef} autoPlay style={{ display: 'none' }} />

      {/* Content row: Main + Sidebar + Insight */}
      <div style={{
        display: 'flex', flexDirection: 'row', flexGrow: 1, overflow: 'hidden', position: 'relative'
      }}>

      {/* Main: Video / Observation / Timeline — single viewport, no scroll */}
      <main style={{
        flexGrow: 1, padding: '8px', overflow: 'hidden', background: BG,
        transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{
          position: 'relative', borderRadius: '12px', overflow: 'hidden',
          background: '#1e1e2d', flex: 1, minHeight: 0
        }}>
          {/* Video feed — always rendered to keep stream alive */}
          <img ref={videoImgRef} alt="Patient feed"
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              display: connectionStatus === 'live' ? 'block' : 'none',
              transform: 'scaleX(-1)',
              opacity: mainView === 'live' ? 1 : 0.12,
              transition: 'opacity 0.3s'
            }} />
          <canvas ref={canvasRef} style={{
            position: 'absolute', top: 0, left: 0,
            pointerEvents: 'none',
            display: connectionStatus === 'live' && mainView === 'live' ? 'block' : 'none'
          }} />

          {/* Waiting / disconnected placeholder */}
          {connectionStatus !== 'live' && mainView === 'live' && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#555', fontSize: '16px'
            }}>
              {sessionEnded
                ? 'Consultation ended. Assessment data is retained for documentation.'
                : patientLeftDeliberately
                  ? 'Patient ended the call. Assessment data is retained.'
                  : connectionStatus === 'waiting' ? 'Waiting for patient to connect...' : 'Disconnected'}
            </div>
          )}

          {/* Keyframes for the overlays below. Defined here rather than reusing
              the sidebar's style block, which sits inside a conditional panel. */}
          <style>{`@keyframes feedPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }`}</style>

          {/* Escalation overlay — subtle, bottom-left of the feed, so the
              clinician keeps the escalation and its ETA in view without it
              covering the patient. */}
          {escalationNotice && mainView === 'live' && (
            <div style={{
              // 62px clears the bottom control bar (~51px tall at bottom:0);
              // at 14px this card rendered behind it and was partly hidden.
              position: 'absolute', left: '14px', bottom: '62px', zIndex: 6,
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 13px', borderRadius: '10px', maxWidth: 'calc(100% - 28px)',
              background: 'rgba(10,12,20,0.66)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(59,130,246,0.45)', pointerEvents: 'none'
            }}>
              <div style={{
                width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                background: '#3b82f6', animation: 'feedPulse 1.6s ease-in-out infinite'
              }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#e5e7eb', fontSize: '11px', fontWeight: '600', letterSpacing: '0.2px' }}>
                  Escalated{escalationNotice.atsLabel ? ` · ATS ${escalationNotice.triageScore} ${escalationNotice.atsLabel}` : ''}
                </div>
                <div style={{ color: '#93c5fd', fontSize: '11px', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {escalationNotice.clinician?.name || 'On-call clinician'}
                  {escalationEta != null && (escalationEta > 0
                    ? ` · joins in ${formatClock(escalationEta)}`
                    : ' · joining now')}
                </div>
              </div>
            </div>
          )}

          {/* Flight tracker overlay — bottom-right, opposite the escalation pill */}
          {flight && mainView === 'live' && (
            <div style={{
              // Same clearance as the escalation pill opposite it.
              position: 'absolute', right: '14px', bottom: '62px', zIndex: 6,
              width: '196px', padding: '9px 12px', borderRadius: '10px',
              background: 'rgba(10,12,20,0.66)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(22,163,74,0.45)', pointerEvents: 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                <span style={{ color: '#e5e7eb', fontSize: '11px', fontWeight: '600', letterSpacing: '0.2px' }}>
                  {flight.phase === 'arrived' ? 'On scene' : flight.phase === 'enroute' ? 'En route' : 'Tasking'}
                </span>
                <span style={{ color: '#6ee7a8', fontSize: '11px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
                  {flight.etaSeconds > 0 ? formatEtaMinutes(flight.etaSeconds) : 'Arrived'}
                </span>
              </div>
              <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${Math.round((flight.progress || 0) * 100)}%`,
                  background: 'linear-gradient(90deg, #16a34a, #6ee7a8)', borderRadius: '2px',
                  transition: 'width 1s linear'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', color: '#8b93a7', fontSize: '10px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{flight.aircraft}</span>
                <span style={{ flexShrink: 0, marginLeft: '6px' }}>{flight.remainingKm} km</span>
              </div>
            </div>
          )}

          {/* Timeline overlay */}
          {mainView === 'timeline' && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(15,15,25,0.92)',
              overflowY: 'auto', padding: '28px 32px',
              animation: 'insightSlideIn 0.25s ease'
            }}>
              <div style={{ height: '28px' }} />
              {analysisHistory.length === 0 ? (
                <p style={{ color: '#555', fontSize: '13px', textAlign: 'left' }}>No observations yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {analysisHistory.slice().reverse().slice(0, 150).map((entry, i) => {
                    if (entry.type === 'transcript') {
                      const isSystem = entry.speaker === 'system';
                      const speakerColor = entry.speaker === 'patient' ? '#38bdf8'
                        : entry.speaker === 'matilda' ? '#6ee7b7'
                        : entry.speaker === 'system' ? '#f97316'
                        : '#a78bfa';
                      const speakerLabel = entry.speaker === 'matilda' ? 'MATILDA'
                        : entry.speaker === 'system' ? 'SYSTEM'
                        : entry.speaker.charAt(0).toUpperCase() + entry.speaker.slice(1);
                      return (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '12px', textAlign: 'left',
                          background: isSystem ? 'rgba(249,115,22,0.08)' : 'rgba(255,255,255,0.02)',
                          border: isSystem ? '1px solid rgba(249,115,22,0.35)' : 'none'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: speakerColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{speakerLabel}</span>
                            <span style={{ color: '#444', fontSize: '10px' }}>{entry.time}</span>
                          </div>
                          <p style={{ margin: 0, color: isSystem ? '#fed7aa' : '#ccc', lineHeight: '1.4' }}>{entry.text}</p>
                        </div>
                      );
                    }
                    if (entry.type === 'bgv-prediction') {
                      return (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '11px', textAlign: 'left',
                          background: 'rgba(255,255,255,0.02)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Prediction</span>
                            <span style={{ color: '#444', fontSize: '10px' }}>{entry.time}</span>
                          </div>
                          <p style={{ margin: 0, color: '#777', lineHeight: '1.4' }}>{entry.content}</p>
                        </div>
                      );
                    }
                    if (entry.type === 'bgv-accuracy') {
                      return (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '11px', textAlign: 'left',
                          background: 'rgba(255,255,255,0.02)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Prediction Accuracy</span>
                            <span style={{ color: '#444', fontSize: '10px' }}>{entry.time}</span>
                          </div>
                          <p style={{ margin: 0, color: '#777', lineHeight: '1.4' }}>{entry.content}</p>
                        </div>
                      );
                    }
                    if (entry.type === 'bgv-changes') {
                      const isHighSurprise = entry.surpriseLevel === 'high';
                      const isMedSurprise = entry.surpriseLevel === 'medium';
                      return (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '11px', textAlign: 'left',
                          background: isHighSurprise ? 'rgba(239,68,68,0.08)' : 'rgba(99,102,241,0.06)',
                          border: `1px solid ${isHighSurprise ? 'rgba(239,68,68,0.25)' : 'rgba(99,102,241,0.15)'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: isHighSurprise ? '#ef4444' : '#8b8bf5', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Changes Observed</span>
                            <span style={{ color: '#555', fontSize: '10px' }}>{entry.time}</span>
                            {(isHighSurprise || isMedSurprise) && (
                              <span style={{
                                fontSize: '8px', padding: '1px 5px', borderRadius: '3px', fontWeight: '700',
                                background: isHighSurprise ? 'rgba(239,68,68,0.2)' : 'rgba(234,179,8,0.15)',
                                color: isHighSurprise ? '#ef4444' : '#eab308'
                              }}>
                                {isHighSurprise ? 'HIGH SURPRISE' : 'MED SURPRISE'}
                              </span>
                            )}
                            {entry.alerts?.medical_concerns && (
                              <span style={{ fontSize: '8px', padding: '1px 5px', borderRadius: '3px', fontWeight: '700', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>MEDICAL</span>
                            )}
                            {entry.alerts?.dynamics_changed && (
                              <span style={{ fontSize: '8px', padding: '1px 5px', borderRadius: '3px', fontWeight: '700', background: 'rgba(234,179,8,0.15)', color: '#eab308' }}>DYNAMICS</span>
                            )}
                            {entry.alerts?.structure_changed && (
                              <span style={{ fontSize: '8px', padding: '1px 5px', borderRadius: '3px', fontWeight: '700', background: 'rgba(99,102,241,0.15)', color: '#8b8bf5' }}>STRUCTURE</span>
                            )}
                          </div>
                          {entry.surpriseReason && (isHighSurprise || isMedSurprise) && (
                            <p style={{ margin: '0 0 4px', color: isHighSurprise ? '#f87171' : '#fbbf24', fontSize: '11px', fontWeight: '600', lineHeight: '1.4' }}>{entry.surpriseReason}</p>
                          )}
                          {entry.content && <p style={{ margin: 0, color: '#ccc', lineHeight: '1.4' }}>{entry.content}</p>}
                        </div>
                      );
                    }
                    if (entry.type === 'bgv-beliefs') {
                      const s = entry.structure;
                      const d = entry.dynamics;
                      const structParts = [s?.location, s?.setting_type, joinList(s?.personnel), joinList(s?.equipment)].filter(Boolean);
                      const dynParts = [d?.patient_posture, d?.current_activity, d?.patient_status, joinList(d?.equipment_states)].filter(Boolean);
                      const vitalEntries = d?.vital_signs ? Object.entries(d.vital_signs).filter(([, v]) => v != null) : [];
                      return (
                        <div key={i} style={{
                          padding: '8px 12px', borderRadius: '8px', fontSize: '11px', textAlign: 'left',
                          background: 'rgba(255,255,255,0.02)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Current Beliefs</span>
                            <span style={{ color: '#444', fontSize: '10px' }}>{entry.time}</span>
                          </div>
                          {entry.summary && <p style={{ margin: '0 0 6px', color: '#888', lineHeight: '1.4', fontSize: '11px' }}>{entry.summary}</p>}
                          {structParts.length > 0 && (
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{ fontSize: '8px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Structure</span>
                              <p style={{ margin: '1px 0 0', color: '#777', lineHeight: '1.4' }}>{structParts.join(' · ')}</p>
                            </div>
                          )}
                          {dynParts.length > 0 && (
                            <div style={{ marginBottom: '4px' }}>
                              <span style={{ fontSize: '8px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Dynamics</span>
                              <p style={{ margin: '1px 0 0', color: '#777', lineHeight: '1.4' }}>{dynParts.join(' · ')}</p>
                            </div>
                          )}
                          {vitalEntries.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '2px' }}>
                              {vitalEntries.map(([k, v]) => {
                                const display = typeof v === 'object' ? (v.value != null ? v.value : JSON.stringify(v)) : v;
                                return (
                                  <span key={k} style={{
                                    fontSize: '9px', padding: '1px 5px', borderRadius: '3px',
                                    background: 'rgba(255,255,255,0.05)', color: '#777', fontWeight: '600'
                                  }}>
                                    {k.toUpperCase()}: {String(display)}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={i} style={{
                        display: 'flex', gap: '12px', alignItems: 'flex-start',
                        padding: '12px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.06)', fontSize: '12px', textAlign: 'left'
                      }}>
                        {entry.thumbnail && (
                          <img src={entry.thumbnail} alt="" style={{
                            width: '100px', height: '75px', borderRadius: '6px', objectFit: 'cover',
                            flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)'
                          }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ color: '#666' }}>{entry.time}</span>
                            {entry.triage && (
                              <span style={{
                                padding: '1px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                                background: `${ATS_COLORS[entry.triage] || '#6c757d'}18`,
                                color: ATS_COLORS[entry.triage] || '#6c757d'
                              }}>ATS {entry.triage}</span>
                            )}
                          </div>
                          <p style={{ margin: 0, color: '#aaa', lineHeight: '1.5' }}>
                            {entry.description.slice(0, 200)}...
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Top-right: view toggle pills */}
          <div style={{
            position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '4px',
            background: 'rgba(0,0,0,0.5)', borderRadius: '8px', padding: '3px', zIndex: 5
          }}>
            {[
              { key: 'live', label: 'Live' },
              { key: 'timeline', label: 'Timeline' }
            ].map(tab => (
              <button key={tab.key} onClick={() => setMainView(tab.key)} style={{
                padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: '600', letterSpacing: '0.2px',
                transition: 'all 0.2s',
                background: mainView === tab.key ? 'rgba(99,102,241,0.8)' : 'transparent',
                color: mainView === tab.key ? '#fff' : '#888'
              }}>{tab.label}</button>
            ))}
          </div>

          {/* Top-left: overlay toggles */}
          {connectionStatus === 'live' && mainView === 'live' && (
            <div style={{ position: 'absolute', top: '10px', left: '10px', display: 'flex', gap: '6px', zIndex: 5 }}>
              <OverlayToggle label="Pose" active={showPose} onClick={() => setShowPose(p => !p)} color="#00ff64" />
              <OverlayToggle label="HR ROI" active={showHrBox} onClick={() => setShowHrBox(p => !p)} color="#ef4444" />
            </div>
          )}

          {/* Primary vitals moved to sidebar */}

          {/* Secondary vitals moved to sidebar */}

          {/* Bottom: analysis controls on the left, clinician controls on the right */}
          <div style={{
            position: 'absolute', bottom: '0', left: '0', right: '0', zIndex: 5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
            flexWrap: 'wrap', rowGap: '8px',
            padding: '14px 16px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
            pointerEvents: 'none'
          }}>
            <div className="ctrl-dock" style={{ pointerEvents: 'auto' }}>
              <ControlBtn
                onClick={analyzeCurrentFrame}
                disabled={isAnalyzing || connectionStatus !== 'live'}
                active={isAnalyzing}
                activeColor="#2563eb"
                icon={isAnalyzing ? <SpinnerIcon /> : <ScanIcon />}
                label={isAnalyzing ? 'Analysing' : 'Analyse'}
              />
              <ControlBtn
                onClick={() => setAutoAnalysis(!autoAnalysis)}
                active={autoAnalysis}
                activeColor="#16a34a"
                icon={<AutoIcon active={autoAnalysis} />}
                label={autoAnalysis ? 'Auto ON' : 'Auto OFF'}
              />
              <ControlBtn
                onClick={() => { setSmsOpen(o => !o); setSmsResult(null); }}
                active={smsOpen}
                activeColor="#2563eb"
                icon={<SmsIcon />}
                label="Invite by SMS"
              />
              <ControlBtn
                onClick={() => { setOncallOpen(o => !o); setOncallResult(null); }}
                active={oncallOpen}
                activeColor="#6366f1"
                icon={<OnCallIcon />}
                label="On-call doctor"
              />
              <ControlBtn
                onClick={sendPlane}
                disabled={!!flight}
                active={!!flight}
                activeColor="#16a34a"
                icon={<PlaneIcon />}
                label={flight
                  ? (flight.phase === 'arrived' ? 'Plane arrived' : `Plane ${formatEtaMinutes(flight.etaSeconds)}`)
                  : 'Send Plane'}
              />
            </div>
            <div className="ctrl-dock" style={{ pointerEvents: 'auto' }}>
              <ControlBtn
                onClick={() => {
                  setAudioMuted(m => {
                    const next = !m;
                    if (next) { stopMicCapture(); } else { startMicCapture(); }
                    return next;
                  });
                }}
                active={audioMuted}
                activeColor="#dc3545"
                icon={audioMuted ? <MicOffIcon /> : <MicOnIcon />}
                label={audioMuted ? 'Mic Off' : 'Mic On'}
              />
              {/* Patient's mic. Label reflects what the patient's client last
                  reported, so it stays honest if they unmute themselves. */}
              <ControlBtn
                onClick={() => setPatientMicMuted(!patientMic.muted)}
                disabled={!patientPresent}
                active={patientMic.muted}
                activeColor="#f59e0b"
                icon={patientMic.muted ? <MicOffIcon /> : <MicOnIcon />}
                label={patientMic.muted
                  ? (patientMic.by === 'patient' ? 'Patient self-muted' : 'Patient muted')
                  : 'Mute patient'}
              />
              <ControlBtn
                onClick={toggleTakeover}
                active={matildaVoice}
                activeColor="#6ee7b7"
                icon={matildaVoice ? <BotIcon /> : <UserIcon />}
                label={matildaVoice ? 'Take Over' : 'Hand Back'}
              />
              {/* Ends the consult for both sides. Separated by a divider so it
                  isn't hit while reaching for Take Over. */}
              <span style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />
              <ControlBtn
                onClick={() => setConfirmEndCall(true)}
                disabled={sessionEnded}
                active={sessionEnded}
                activeColor="#dc3545"
                danger
                title="End the consult for both sides"
                icon={<EndCallIcon />}
                label={sessionEnded ? 'Call ended' : 'End Call'}
              />
            </div>
          </div>

          {/* SMS invite panel. The clinician types the patient's mobile and the
              server texts them a single-use, room-scoped link — no password to
              read out over a bad phone connection. */}
          {confirmEndCall && (
            <div
              onClick={() => setConfirmEndCall(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: '380px', padding: '20px 22px', borderRadius: '14px',
                  background: '#16181c', border: '1px solid rgba(255,255,255,0.12)',
                  textAlign: 'left', boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
                }}
              >
                <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>
                  End this consultation?
                </div>
                <div style={{ color: '#8b93a7', fontSize: '12px', marginBottom: '16px', lineHeight: '1.55' }}>
                  The patient will be told the call has ended and their camera and microphone
                  will turn off. Your transcript, vitals and assessment stay on screen so you
                  can finish documenting.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    onClick={endCall}
                    style={{
                      padding: '11px', borderRadius: '9px', border: 'none', background: '#dc3545',
                      color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                    }}
                  >End call</button>
                  <button
                    onClick={() => setConfirmEndCall(false)}
                    style={{
                      padding: '11px', borderRadius: '9px', border: '1px solid rgba(255,255,255,0.14)',
                      background: 'transparent', color: '#c7d2e5', fontSize: '13px',
                      fontWeight: '600', cursor: 'pointer'
                    }}
                  >Keep the call going</button>
                </div>
              </div>
            </div>
          )}

          {/* Who gets paged when this consult escalates.
              This used to live ONLY inside the escalation panel, which does not
              render until an escalation is already running — so the number could
              not be set until it was too late to matter. It belongs here, beside
              the patient invite, before anything has gone wrong. */}
          {oncallOpen && (
            <div
              onClick={() => setOncallOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: '400px', padding: '20px 22px', borderRadius: '14px',
                  background: '#16181c', border: '1px solid rgba(255,255,255,0.12)',
                  textAlign: 'left', boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
                }}
              >
                <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>
                  Escalation contacts
                </div>
                <div style={{ color: '#8b93a7', fontSize: '12px', marginBottom: '14px', lineHeight: '1.5' }}>
                  Who MATILDA texts if this consult escalates. Remembered on this
                  computer, so set them once at the start of your shift.
                </div>
                <div style={{ color: '#8b93a7', fontSize: '11px', fontWeight: '600', marginBottom: '5px' }}>
                  On-call doctor
                </div>
                <form
                  onSubmit={(e) => { e.preventDefault(); sendOncallLink(); }}
                  style={{ display: 'flex', gap: '8px' }}
                >
                  <input
                    type="tel"
                    inputMode="tel"
                    value={oncallPhone}
                    onChange={(e) => setOncallPhone(e.target.value)}
                    placeholder="0459 567 974"
                    autoFocus
                    style={{
                      flex: 1, minWidth: 0, padding: '11px 13px', borderRadius: '9px',
                      border: '1px solid rgba(255,255,255,0.14)', background: '#1f2229',
                      color: '#fff', fontSize: '14px', outline: 'none'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={oncallSending || !oncallPhone.trim()}
                    style={{
                      padding: '11px 17px', borderRadius: '9px', border: 'none',
                      background: oncallSending || !oncallPhone.trim() ? 'rgba(99,102,241,0.4)' : '#6366f1',
                      color: '#fff', fontSize: '13px', fontWeight: '600',
                      cursor: oncallSending || !oncallPhone.trim() ? 'default' : 'pointer', whiteSpace: 'nowrap'
                    }}
                  >{oncallSending ? 'Sending…' : 'Send link now'}</button>
                </form>
                <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '9px', lineHeight: '1.5' }}>
                  Saving the number is enough — an escalation texts it automatically.
                  “Send link now” only pages them immediately.
                </div>

                <div style={{ height: '1px', background: 'rgba(255,255,255,0.10)', margin: '16px 0 14px' }} />

                <div style={{ color: '#8b93a7', fontSize: '11px', fontWeight: '600', marginBottom: '5px' }}>
                  Medical chest custodian
                </div>
                <input
                  type="tel"
                  inputMode="tel"
                  value={medchestPhone}
                  onChange={(e) => setMedchestPhone(e.target.value)}
                  placeholder="0498 231 894"
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: '9px',
                    border: '1px solid rgba(255,255,255,0.14)', background: '#1f2229',
                    color: '#fff', fontSize: '14px', outline: 'none'
                  }}
                />
                <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '9px', lineHeight: '1.5' }}>
                  Texted at ATS 1–2 so the chest is open before the doctor phones. The
                  message carries no patient detail. Leave blank to use the site default.
                </div>
                {oncallResult && (
                  <div style={{
                    marginTop: '11px', fontSize: '12px', lineHeight: '1.5',
                    color: oncallResult.ok ? '#6ee7a8' : '#ff8d99',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                  }}>{oncallResult.message}</div>
                )}
                <button
                  onClick={() => setOncallOpen(false)}
                  style={{
                    marginTop: '14px', width: '100%', padding: '10px', borderRadius: '9px',
                    border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
                    color: '#c7d2e5', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}
                >Close</button>
              </div>
            </div>
          )}

          {smsOpen && (
            <div
              onClick={() => setSmsOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: '400px', padding: '20px 22px', borderRadius: '14px',
                  background: '#16181c', border: '1px solid rgba(255,255,255,0.12)',
                  textAlign: 'left', boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
                }}
              >
                <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>
                  Send join link by SMS
                </div>
                <div style={{ color: '#8b93a7', fontSize: '12px', marginBottom: '14px', lineHeight: '1.5' }}>
                  Single-use link for room “{roomId}”, valid 30 minutes. The patient taps it to
                  join — no password to read out.
                </div>
                <form
                  onSubmit={(e) => { e.preventDefault(); sendPatientLink(); }}
                  style={{ display: 'flex', gap: '8px' }}
                >
                  <input
                    type="tel"
                    inputMode="tel"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    placeholder="0412 345 678"
                    autoFocus
                    style={{
                      flex: 1, minWidth: 0, padding: '11px 13px', borderRadius: '9px',
                      border: '1px solid rgba(255,255,255,0.14)', background: '#1f2229',
                      color: '#fff', fontSize: '14px', outline: 'none'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={smsSending || !smsPhone.trim()}
                    style={{
                      padding: '11px 17px', borderRadius: '9px', border: 'none',
                      background: smsSending || !smsPhone.trim() ? 'rgba(37,99,235,0.4)' : '#2563eb',
                      color: '#fff', fontSize: '13px', fontWeight: '600',
                      cursor: smsSending || !smsPhone.trim() ? 'default' : 'pointer', whiteSpace: 'nowrap'
                    }}
                  >{smsSending ? 'Sending…' : 'Send link'}</button>
                </form>
                {smsResult && (
                  <div style={{
                    marginTop: '11px', fontSize: '12px', lineHeight: '1.5',
                    color: smsResult.ok ? '#6ee7a8' : '#ff8d99',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                  }}>{smsResult.message}</div>
                )}
                <button
                  onClick={() => setSmsOpen(false)}
                  style={{
                    marginTop: '14px', width: '100%', padding: '10px', borderRadius: '9px',
                    border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
                    color: '#c7d2e5', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}
                >Close</button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Sidebar */}
      <aside style={{
        width: insightOpen ? '300px' : '360px', flexShrink: 0,
        // Bottom padding clears the fixed Insight button (52px at bottom:24px),
        // which floats over this sidebar's lower-right corner. Without it,
        // whatever ends up last in the sidebar sits underneath it — currently
        // the Session panel's invite controls.
        background: BG, borderLeft: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 88px', overflowY: 'auto',
        transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)', textAlign: 'left'
      }}>

        <EmergencyAccessDialog
          open={emergencyDialogOpen}
          busy={emergencyRecording}
          onConfirm={confirmEmergencyAccess}
          onCancel={() => setEmergencyDialogOpen(false)}
        />

        {/* --- MATILDA Escalation Agent (only when active and not dismissed) --- */}
        {escalationActive && !escalationDismissed && escalationSteps.length > 0 && (() => {
          const sorted = [...escalationSteps].sort((a, b) => new Date(a.time) - new Date(b.time));
          const lastStep = sorted[sorted.length - 1];
          const allDone = lastStep && lastStep.id === 'connect' && lastStep.status === 'done';
          const handedOver = matildaVoice === false;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 10px' }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', background: '#ef4444',
                  animation: allDone ? 'none' : 'identityPulse 1.4s ease-in-out infinite'
                }} />
                <h3 style={{
                  fontSize: '11px', color: '#ef4444', margin: 0,
                  textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, flex: 1, textAlign: 'left'
                }}>
                  {allDone ? 'Clinician Handover' : 'Escalation'}
                </h3>
                <span style={{ fontSize: '9px', color: '#555', fontWeight: 600 }}>
                  {sorted.length} step{sorted.length === 1 ? '' : 's'}
                </span>
                <button
                  onClick={() => setEscalationDismissed(true)}
                  title="Dismiss"
                  style={{
                    background: 'transparent', border: 'none', color: '#666', cursor: 'pointer',
                    padding: '0 2px', fontSize: '14px', lineHeight: 1
                  }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                {sorted.map(step => {
                  const isRunning = step.status === 'running';
                  const isDone = step.status === 'done';
                  return (
                    <div key={step.id + step.time} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', textAlign: 'left' }}>
                      <div style={{
                        marginTop: '2px',
                        width: '12px', height: '12px', flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isDone ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                        border: `1px solid ${isDone ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.5)'}`
                      }}>
                        {isDone && <span style={{ color: '#22c55e', fontSize: '8px', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                        {isRunning && <span style={{
                          width: '4px', height: '4px', borderRadius: '50%',
                          background: '#ef4444', animation: 'pulse 1s infinite'
                        }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: isRunning ? '#ef4444' : isDone ? '#eee' : '#888', fontWeight: isRunning ? 600 : 500, lineHeight: 1.4 }}>
                          {step.label}
                        </div>
                        {step.note && (
                          <div style={{ fontSize: '10px', color: '#c7d2e5', lineHeight: 1.45, marginTop: '4px' }}>
                            {step.note}
                          </div>
                        )}
                        {step.detail && (
                          <div style={{ fontSize: '10px', color: '#666', marginTop: '1px', lineHeight: 1.4 }}>{step.detail}</div>
                        )}
                        {step.download && (
                          <button
                            onClick={() => downloadEscalationIsbar(step.download, step.filename)}
                            style={{
                              marginTop: '5px', padding: '4px 9px', borderRadius: '5px', cursor: 'pointer',
                              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.45)',
                              color: '#22c55e', fontSize: '10px', fontWeight: 700, letterSpacing: '0.2px',
                              display: 'flex', alignItems: 'center', gap: '5px'
                            }}>
                            ↓ ISBAR handover (.docx)
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Who was paged, and a way to page them again */}
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '9px', color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                  On-call doctor
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <input
                    value={oncallPhone}
                    onChange={e => setOncallPhone(e.target.value)}
                    placeholder="0459 567 974"
                    inputMode="tel"
                    style={{
                      flex: 1, minWidth: 0, background: 'rgba(0,0,0,0.3)', color: '#ddd',
                      border: '1px solid rgba(255,255,255,0.12)', borderRadius: '5px',
                      padding: '5px 7px', fontSize: '11px'
                    }} />
                  <button
                    onClick={sendOncallLink}
                    disabled={oncallSending}
                    style={{
                      padding: '5px 9px', borderRadius: '5px', whiteSpace: 'nowrap',
                      background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.45)',
                      color: '#a5b4fc', fontSize: '10px', fontWeight: 700,
                      cursor: oncallSending ? 'default' : 'pointer', opacity: oncallSending ? 0.5 : 1
                    }}>
                    {oncallSending ? 'Sending…' : 'Resend link'}
                  </button>
                </div>
                {oncallResult && (
                  <div style={{ fontSize: '9px', marginTop: '4px', color: oncallResult.ok ? '#22c55e' : '#ef4444' }}>
                    {oncallResult.message}
                  </div>
                )}
              </div>

              <button
                onClick={toggleTakeover}
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: '6px',
                  background: 'transparent',
                  border: `1px solid ${handedOver ? 'rgba(34,197,94,0.5)' : 'rgba(239,68,68,0.55)'}`,
                  color: handedOver ? '#22c55e' : '#ef4444',
                  fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                  letterSpacing: '0.3px', textTransform: 'uppercase'
                }}>
                {handedOver ? 'Hand Back to MATILDA' : 'Take Over Consult'}
              </button>
              <Divider />
            </>
          );
        })()}

        {/* --- Patient identity verification (top of sidebar) --- */}
        <SidebarHeading>Patient Identity</SidebarHeading>
        <IdentityCard
          thumbnail={identityThumbnail}
          status={identityStatus}
          absentSeconds={identityAbsentSeconds}
          confidence={identityConfidence}
          reason={identityReason}
          checkedAt={identityCheckedAt}
          failures={identityFailures}
          onReenrol={() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'reenrol_identity' }));
            }
            setIdentityStatus('enrolling');
            setIdentityThumbnail(null);
            setIdentityFailures(0);
            setIdentityConfidence(null);
            setIdentityReason(null);
          }}
        />

        <Divider />

        {/* --- Triage + ABCDE --- */}
        <SidebarHeading>Triage</SidebarHeading>
        {triageScore ? (() => {
          const ATS_MAX_WAIT = { 1: 'Immediate', 2: '10 min', 3: '30 min', 4: '60 min', 5: '120 min' };
          const prevScore = triageHistory.length > 1 ? triageHistory[triageHistory.length - 2].score : null;
          const trend = prevScore ? (triageScore < prevScore ? 'up' : triageScore > prevScore ? 'down' : 'stable') : null;
          return (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '10px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${triageColor}18`, border: `2px solid ${triageColor}55`
              }}>
                <span style={{ fontSize: '28px', fontWeight: '800', color: triageColor, lineHeight: 1 }}>{triageScore}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span style={{ fontSize: '13px', color: triageColor, fontWeight: '700' }}>{ATS_LABELS[triageScore]}</span>
                  {triageConfidence && (
                    <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', color: '#888', fontWeight: '600' }}>{triageConfidence}%</span>
                  )}
                  <span style={{ fontSize: '10px', color: '#555', marginLeft: 'auto' }}>{ATS_MAX_WAIT[triageScore]}</span>
                </div>
                {triageReasoning && (
                  <p style={{ fontSize: '11px', color: '#eee', margin: '0 0 5px', lineHeight: '1.4' }}>{triageReasoning}</p>
                )}
                {/* Triage history shown in observation chart */}
              </div>
            </div>
          );
        })() : (
          <p style={{ fontSize: '11px', color: '#555', margin: '0 0 10px', textAlign: 'left' }}>Awaiting analysis</p>
        )}
        <ABCDEBar findings={assessmentResults?.stage?.abcde_findings} overallPct={assessmentResults?.stage?.overall_completeness_percentage} />

        <Divider />

        {/* --- Vitals --- */}
        <SidebarHeading>Vitals</SidebarHeading>
        {(() => {
          // Calibration: hide HR and RR until the pipeline reports each estimator
          // settled, so the clinician never reads a warm-up value as a finding.
          // These are the same flags that gate what reaches triage and the AI.
          const warmupSeconds = vitalsStatus?.warmupSeconds || CALIBRATION_FALLBACK_MS / 1000;
          const calibratingProgress = {
            hr: vitalsStatus?.hr?.elapsedSeconds ?? 0,
            rr: vitalsStatus?.br?.elapsedSeconds ?? 0
          };
          const formatAge = (ts) => {
            if (!ts) return null;
            const s = Math.round((Date.now() - ts) / 1000);
            if (s < 5) return 'now';
            if (s < 60) return `${s}s ago`;
            const m = Math.round(s / 60);
            return `${m}m ago`;
          };
          const coreVitals = [
            { label: 'HR', value: vitals.bpm, unit: 'bpm', color: '#ef4444', red: v => v > 130 || v < 40, yellow: v => v > 110 || v < 50, key: 'hr', updatedAt: vitalUpdatedAt.hr },
            { label: 'RR', value: vitals.breathingRate, unit: 'br/m', color: '#3b82f6', red: v => v >= 25 || v <= 8, yellow: v => v >= 21 || v <= 11, key: 'rr', updatedAt: vitalUpdatedAt.rr },
            { label: 'SpO₂', value: vitals.spo2?.value, unit: '%', color: '#8b5cf6', red: v => v <= 91, yellow: v => v <= 95, key: 'spo2', updatedAt: vitalUpdatedAt.spo2 },
          ];
          const extraVitals = [
            // The bloodPressure object now arrives as soon as BP is enabled, so the
            // tile must key off an actual reading rather than the object's presence.
            ...(vitals.bp?.systolic && vitals.bp?.diastolic ? [{ label: 'BP', value: `${vitals.bp.systolic}/${vitals.bp.diastolic}`, unit: 'mmHg', color: '#f59e0b', red: () => false, yellow: () => false, key: 'sbp' }] : []),
            ...(vitals.bmi ? [{ label: 'BMI', value: vitals.bmi, unit: '', color: '#10b981', red: () => false, yellow: () => false }] : []),
            ...(emotion.dominant ? [{ label: 'Affect', value: emotion.dominant, unit: '', color: '#ddd', red: () => false, yellow: () => false, isText: true }] : []),
          ];
          const lastObs = rdrObservations.length > 1 ? rdrObservations.slice(-4, -1) : [];
          return (
            <>
              {/* Core vitals: HR / RR / SpO₂ side-by-side */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: extraVitals.length ? '8px' : '4px' }}>
                {coreVitals.map(v => {
                  // A withheld reading (movement too close to the rate we are
                  // trying to measure) is shown as no reading rather than left
                  // at its last value — a stale number with nothing to explain
                  // it is how a wrong number gets read as a measurement.
                  const withheld = (v.key === 'hr' && vitalsStatus?.hr?.unreliable)
                    || (v.key === 'rr' && vitalsStatus?.br?.unreliable);
                  if (withheld) v = { ...v, value: null };
                  const calibrating = v.key !== 'spo2' && !vitalsSettled[v.key];
                  const severity = v.value != null && !calibrating ? (v.red(v.value) ? '#ef4444' : v.yellow(v.value) ? '#eab308' : '#22c55e') : null;
                  const age = formatAge(v.updatedAt);
                  return (
                    <div key={v.label} style={{
                      flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px',
                      padding: '4px 0', textAlign: 'left'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '10px', color: '#555', fontWeight: '600' }}>{v.label}</span>
                        {severity && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: severity, flexShrink: 0 }} />}
                      </div>
                      {calibrating ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic', fontWeight: 500 }}>
                              Calibrating…
                            </span>
                            {v.value != null && v.value !== 0 && (
                              <span style={{ fontSize: '10px', color: '#444', fontVariantNumeric: 'tabular-nums' }}>
                                ({v.value})
                              </span>
                            )}
                          </div>
                          <div style={{ height: '2px', background: 'rgba(255,255,255,0.08)', borderRadius: '1px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min(100, ((calibratingProgress[v.key] || 0) / warmupSeconds) * 100)}%`,
                              background: '#6366f1', transition: 'width 0.4s'
                            }} />
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: v.value != null ? v.color : '#333', fontVariantNumeric: 'tabular-nums' }}>
                            {v.value != null ? v.value : '—'}
                          </span>
                          {v.unit && <span style={{ fontSize: '9px', color: '#444' }}>{v.unit}</span>}
                        </div>
                      )}
                      {!calibrating && age && (
                        <div style={{ fontSize: '9px', color: '#555' }}>{age}</div>
                      )}
                      {!calibrating && lastObs.length > 0 && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {lastObs.map((obs, i) => {
                            const val = obs[v.key];
                            return <span key={i} style={{ fontSize: '9px', color: '#444', fontVariantNumeric: 'tabular-nums' }}>{val ?? '·'}</span>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Why BP is unavailable. PTT needs a hand in frame alongside the
                  face, so without this the tile is just silently absent and the
                  clinician has no way to know the patient needs to raise a hand. */}
              {vitals.bp && !(vitals.bp.systolic && vitals.bp.diastolic) && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px',
                  padding: '5px 8px', borderRadius: '6px',
                  background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.28)'
                }}>
                  <span style={{ color: '#f59e0b', fontSize: '9px', fontWeight: '700', letterSpacing: '0.4px' }}>BP</span>
                  <span style={{ color: '#8b93a7', fontSize: '10px', flex: 1, minWidth: 0 }}>
                    {!vitals.bp.faceDetected
                      ? 'No face detected'
                      : !vitals.bp.handDetected
                        ? 'Ask patient to hold a hand up beside their face'
                        : `Measuring — ${vitals.bp.progress ?? 0}%`}
                  </span>
                  <span style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                    {[['Face', vitals.bp.faceDetected], ['Hand', vitals.bp.handDetected]].map(([label, ok]) => (
                      <span key={label} style={{
                        fontSize: '9px', fontWeight: '600', padding: '1px 5px', borderRadius: '4px',
                        background: ok ? 'rgba(34,197,94,0.18)' : 'rgba(120,120,130,0.18)',
                        color: ok ? '#22c55e' : '#777'
                      }}>{label}</span>
                    ))}
                  </span>
                </div>
              )}

              {/* Conditional extras: BP / BMI / Affect — stacked rows in original layout */}
              {extraVitals.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', marginBottom: '4px' }}>
                  {extraVitals.map(v => {
                    const severity = v.value != null && !v.isText ? (v.red(v.value) ? '#ef4444' : v.yellow(v.value) ? '#eab308' : '#22c55e') : null;
                    return (
                      <div key={v.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                        <span style={{ fontSize: '10px', color: '#555', fontWeight: '600', width: '34px', flexShrink: 0 }}>{v.label}</span>
                        {severity && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: severity, flexShrink: 0 }} />}
                        <span style={{ fontSize: '15px', fontWeight: '700', color: v.value != null ? v.color : '#333', fontVariantNumeric: 'tabular-nums', minWidth: '40px' }}>
                          {v.value != null ? v.value : '—'}
                        </span>
                        {v.unit && <span style={{ fontSize: '9px', color: '#444' }}>{v.unit}</span>}
                        {!v.isText && v.key && lastObs.length > 0 && (
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {lastObs.map((obs, i) => {
                              const val = obs[v.key];
                              return <span key={i} style={{ fontSize: '9px', color: '#444', fontVariantNumeric: 'tabular-nums' }}>{val ?? '·'}</span>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}

        <SignalDiagnostics
          dataRef={overlayDataRef}
          diag={diag}
          spectrum={spectrum}
          bpm={vitals.bpm}
          open={diagOpen}
          onToggle={() => setDiagOpen(o => !o)}
          panelsOn={diagPanelsOn}
          onPanelsToggle={toggleDiagPanels}
        />

        <Divider />

        {/* Sits with Vitals rather than with the record tiers: this IS the
            live measurement, and it reads better beside the numbers it
            summarises than three sections further down. */}
        {/* LEVEL 3 — measured in this consult, minutes old */}
        <RecordLevel
          level="3"
          title="Data captured today"
          source="This consult · camera, conversation and assessment"
          status={connectionStatus === 'live' ? 'Live' : 'Ended'}
          statusColour={connectionStatus === 'live' ? '#22c55e' : '#555'}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px' }}>
            {[
              ['Vitals', [
                vitalsSettled.hr && vitals.bpm ? `HR ${vitals.bpm}` : null,
                vitalsSettled.rr && vitals.breathingRate ? `RR ${vitals.breathingRate}` : null,
                vitals.spo2?.value ? `SpO₂ ${vitals.spo2.value}%` : null,
                // Guard on the READING, not the object: the bp payload exists
                // from the moment BP is enabled, so `vitals.bp ?` alone printed
                // "BP null/null" for the whole consult.
                vitals.bp?.systolic && vitals.bp?.diastolic
                  ? `BP ${vitals.bp.systolic}/${vitals.bp.diastolic}` : null
              ].filter(Boolean).join(' · ') || 'calibrating'],
              ['Triage', triageScore ? `ATS ${triageScore}` : 'not yet scored'],
              ['ABCDE', assessmentResults?.stage?.overall_completeness_percentage != null
                ? `${Math.round(assessmentResults.stage.overall_completeness_percentage)}% complete`
                : 'not yet assessed'],
              ['Conversation', transcript.length ? `${transcript.length} exchanges` : 'none yet'],
              ['Observation', frameDescription ? `${frameDescription.slice(0, 70)}…` : 'none yet']
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                <span style={{ fontSize: '9px', color: '#555', fontWeight: 600, width: '76px', flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: '10px', color: '#bbb', lineHeight: 1.45 }}>{value}</span>
              </div>
            ))}
          </div>
        </RecordLevel>

        <Divider />

        {/* --- Observation Chart --- */}
        <SidebarHeading>Observation Chart</SidebarHeading>
        {rdrObservations.length > 0 && (
          <>
            <div onClick={() => setRdrExpanded(e => !e)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginBottom: rdrExpanded ? '6px' : '0'
            }}>
              <span style={{ fontSize: '10px', color: '#666' }}>{rdrObservations.length} readings</span>
              <span style={{ fontSize: '8px', color: '#555', marginLeft: 'auto', transition: 'transform 0.2s', transform: rdrExpanded ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
            </div>
            {rdrExpanded && (() => {
              const params = [
                { key: 'hr', label: 'HR', red: v => v > 130 || v < 40, yellow: v => v > 110 || v < 50 },
                { key: 'rr', label: 'RR', red: v => v >= 25 || v <= 8, yellow: v => v >= 21 || v <= 11 },
                { key: 'spo2', label: 'SpO₂', red: v => v <= 91, yellow: v => v <= 95 },
                { key: 'sbp', label: 'SBP', red: v => v >= 180 || v <= 90, yellow: v => v >= 160 || v <= 100 },
                { key: 'dbp', label: 'DBP', red: v => v >= 110 || v <= 50, yellow: v => v >= 100 || v <= 60 },
                { key: '_sep' },
                { key: 'gcs', label: 'GCS', red: v => v <= 8, yellow: v => v <= 12, llm: true },
                { key: 'avpu', label: 'AVPU', red: v => v === 'Unresponsive' || v === 'Pain', yellow: v => v === 'Verbal', isText: true, llm: true },
                { key: 'painScore', label: 'Pain', red: v => v >= 7, yellow: v => v >= 4, llm: true },
                { key: 'temp', label: 'Temp', red: v => v >= 39 || v <= 35, yellow: v => v >= 38 || v <= 36, llm: true },
                { key: 'bgl', label: 'BGL', red: v => v <= 3 || v >= 20, yellow: v => v <= 4 || v >= 15, llm: true },
              ];
              const recent = rdrObservations.slice(-5);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', gap: '0', paddingLeft: '38px', marginBottom: '2px' }}>
                    {recent.map((obs, i) => (
                      <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '8px', color: '#444' }}>{obs.time}</span>
                    ))}
                  </div>
                  {params.map(p => {
                    if (p.key === '_sep') return <div key="_sep" style={{ height: '1px', background: 'rgba(99,102,241,0.1)', margin: '3px 0' }} />;
                    return (
                      <div key={p.key} style={{ display: 'flex', alignItems: 'center', height: '20px' }}>
                        <span style={{ width: '34px', flexShrink: 0, fontSize: '9px', fontWeight: '600', color: p.llm ? '#6366f1' : '#555' }}>{p.label}</span>
                        <div style={{ flex: 1, display: 'flex', gap: '0' }}>
                          {recent.map((obs, i) => {
                            const v = obs[p.key];
                            const bg = v != null && v !== '' ? (p.red(v) ? 'rgba(239,68,68,0.2)' : p.yellow(v) ? 'rgba(234,179,8,0.15)' : 'rgba(34,197,94,0.08)') : 'transparent';
                            return (
                              <span key={i} style={{
                                flex: 1, textAlign: 'center', fontSize: '10px', fontWeight: '600',
                                color: v != null ? '#ccc' : '#333', background: bg, borderRadius: '2px',
                                padding: '1px 0', margin: '0 1px'
                              }} title={p.isText && v ? String(v) : undefined}>
                                {v != null ? (p.isText ? String(v).charAt(0) : v) : '·'}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {rdrObservations.some(o => o.triage) && (
                    <div style={{ display: 'flex', alignItems: 'center', height: '20px', marginTop: '2px' }}>
                      <span style={{ width: '34px', flexShrink: 0, fontSize: '9px', fontWeight: '600', color: '#555' }}>ATS</span>
                      <div style={{ flex: 1, display: 'flex', gap: '0' }}>
                        {recent.map((obs, i) => {
                          const atsColors = { 1: '#dc2626', 2: '#ea580c', 3: '#eab308', 4: '#22c55e', 5: '#3b82f6' };
                          return (
                            <span key={i} style={{
                              flex: 1, textAlign: 'center', fontSize: '10px', fontWeight: '700',
                              color: obs.triage ? atsColors[obs.triage] : '#333', padding: '1px 0', margin: '0 1px'
                            }}>{obs.triage || '·'}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
        {rdrObservations.length === 0 && (
          <p style={{ fontSize: '11px', color: '#444', margin: '0 0 4px' }}>Awaiting readings</p>
        )}

        <Divider />

        {/* --- Belief State --- */}
        <SidebarHeading>Belief State</SidebarHeading>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: bgvExpanded ? '8px' : '0' }}>
          <h3 onClick={() => setBgvExpanded(e => !e)} style={{
            fontSize: '10px', color: '#666', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            {bgvLoading ? 'Analysing...' : bgvState.initialized ? `Frame ${bgvState.frameCount}` : 'Awaiting first frame'}
            <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: bgvExpanded ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
          </h3>
          <button onClick={() => {
            setBgvEnabled(e => !e);
            if (bgvEnabled) setBgvState({ initialized: false, frameCount: 0, structure: null, dynamics: null, precision: { structure: 0.5, dynamics: 0.5 }, lastObservation: null, changesDetected: null, surpriseLevel: 'low', surpriseReason: '', prediction: null, predictionAccuracy: null, beliefsSummary: null });
          }} style={{
            padding: '2px 8px', borderRadius: '10px', border: 'none', fontSize: '9px', fontWeight: '600',
            background: bgvEnabled ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.06)',
            color: bgvEnabled ? '#8b8bf5' : '#555', cursor: 'pointer', transition: 'all 0.15s'
          }}>
            {bgvEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
        {bgvExpanded && bgvEnabled && (() => {
          const surprise = bgvState.surpriseLevel || 'low';
          const surpriseLabel = { low: 'Low', medium: 'Med', high: 'High' }[surprise] || surprise;
          const surpriseColor = surprise === 'high' ? '#ef4444' : surprise === 'medium' ? '#eab308' : '#22c55e';
          const surpriseReason = bgvState.surpriseReason || '';
          const sPrec = bgvState.precision?.structure || 0.5;
          const dPrec = bgvState.precision?.dynamics || 0.5;
          const sPrecColor = sPrec >= 0.7 ? '#22c55e' : sPrec >= 0.4 ? '#eab308' : '#ef4444';
          const dPrecColor = dPrec >= 0.7 ? '#22c55e' : dPrec >= 0.4 ? '#eab308' : '#ef4444';
          return (
            <div style={{ marginBottom: '4px' }}>
              {/* Surprise — same layout as triage card */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{
                  width: '52px', height: '52px', flexShrink: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', borderRadius: '10px',
                  background: `${surpriseColor}12`, border: `2px solid ${surpriseColor}40`,
                  animation: surprise === 'high' ? 'surprisePulse 2s ease-in-out infinite' : 'none'
                }}>
                  <span style={{ fontSize: '16px', fontWeight: '800', color: surpriseColor, lineHeight: 1 }}>
                    {surpriseLabel}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                    <span style={{ fontSize: '12px', color: surpriseColor, fontWeight: '700' }}>Surprise</span>
                    <span style={{ fontSize: '10px', color: '#555' }}>
                      {bgvLoading ? 'Analysing...' : bgvState.initialized ? `Frame ${bgvState.frameCount}` : ''}
                    </span>
                  </div>
                  {surpriseReason && surprise !== 'low' ? (
                    <p style={{ fontSize: '11px', color: '#eee', margin: 0, lineHeight: 1.4 }}>{surpriseReason}</p>
                  ) : (
                    <p style={{ fontSize: '11px', color: '#555', margin: 0, lineHeight: 1.4 }}>
                      {bgvState.initialized ? 'Scene matches predictions' : 'Awaiting analysis'}
                    </p>
                  )}
                </div>
              </div>

              {/* Beliefs detail */}
              <div style={{ textAlign: 'left' }}>
                {/* Structure */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#888', fontWeight: '700' }}>Structure</span>
                    <span style={{ fontSize: '9px', color: sPrecColor, fontWeight: '600' }}>{Math.round(sPrec * 100)}%</span>
                  </div>
                  {bgvState.structure ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {bgvState.structure.location && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Location</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{bgvState.structure.location}</span>
                        </div>
                      )}
                      {bgvState.structure.setting_type && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Setting</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{bgvState.structure.setting_type}</span>
                        </div>
                      )}
                      {bgvState.structure.personnel?.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>People</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{joinList(bgvState.structure.personnel)}</span>
                        </div>
                      )}
                      {bgvState.structure.equipment?.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Equip.</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{joinList(bgvState.structure.equipment)}</span>
                        </div>
                      )}
                    </div>
                  ) : <span style={{ fontSize: '11px', color: '#444' }}>Awaiting data</span>}
                </div>

                {/* Dynamics */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#888', fontWeight: '700' }}>Dynamics</span>
                    <span style={{ fontSize: '9px', color: dPrecColor, fontWeight: '600' }}>{Math.round(dPrec * 100)}%</span>
                  </div>
                  {bgvState.dynamics ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {bgvState.dynamics.patient_posture && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Posture</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{bgvState.dynamics.patient_posture}</span>
                        </div>
                      )}
                      {bgvState.dynamics.current_activity && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Activity</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{bgvState.dynamics.current_activity}</span>
                        </div>
                      )}
                      {bgvState.dynamics.patient_status && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Status</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{bgvState.dynamics.patient_status}</span>
                        </div>
                      )}
                      {joinList(bgvState.dynamics.equipment_states) && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={{ fontSize: '9px', color: '#555', fontWeight: '600', width: '58px', flexShrink: 0 }}>Equip.</span>
                          <span style={{ fontSize: '11px', color: '#ccc' }}>{joinList(bgvState.dynamics.equipment_states)}</span>
                        </div>
                      )}
                    </div>
                  ) : <span style={{ fontSize: '11px', color: '#444' }}>Awaiting data</span>}
                </div>
              </div>
            </div>
          );
        })()}

        <style>{`
          @keyframes surprisePulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.3); }
            50% { box-shadow: 0 0 12px 4px rgba(239,68,68,0.15); }
          }
          @keyframes escalationPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
            50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.8); }
          }
          @keyframes ctrlSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        <Divider />

        {/* ============ Three levels of patient information ============ */}
        {/* LEVEL 1 — the national record, retrieved over the FHIR gateway */}
        <RecordLevel
          level="1"
          title="My Health Record"
          logo="/logos/my-health-record.svg"
          source={mhrSource?.gateway
            ? `${mhrSource.gateway}${mhrSource.accessType === 'EmergencyAccess' ? ' · EMERGENCY ACCESS' : ''}`
            : 'National record · retrieved over the FHIR gateway'}
          status={mhrError ? 'Failed' : mhrSource?.retrievedAt ? 'Retrieved' : isLoadingRecord ? 'Receiving' : 'Idle'}
          statusColour={mhrError ? '#ef4444' : mhrSource?.retrievedAt ? '#22c55e' : isLoadingRecord ? '#6366f1' : '#555'}
        />

        {/* --- My Health Record gateway feed --- */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: mhrExpanded ? '8px' : '0' }}>
          <h3 onClick={() => setMhrExpanded(e => !e)} style={{
            fontSize: '11px', color: '#666', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            Gateway retrieval
            <span style={{ fontSize: '9px', transition: 'transform 0.2s', transform: mhrExpanded ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
          </h3>
          <span style={{ fontSize: '9px', color: mhrError ? '#ef4444' : mhrSource?.retrievedAt ? '#22c55e' : isLoadingRecord ? '#6366f1' : '#555' }}>
            {mhrError ? 'Failed' : mhrSource?.retrievedAt ? 'Retrieved' : isLoadingRecord ? 'Receiving...' : 'Idle'}
          </span>
        </div>
        {mhrExpanded && (
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '9px', color: '#555', marginBottom: '6px', lineHeight: '1.5' }}>
              {mhrSource?.gateway || 'MHR FHIR Gateway v2.0.0 · FHIR 1.4.0'}
              <br />IHI {DEMO_IHI}{mhrSource?.logicalId ? ` · record ${mhrSource.logicalId}` : ''}
            </div>

            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              {['GeneralAccess', 'EmergencyAccess'].map(type => (
                <button key={type} onClick={() => {
                  if (type === 'EmergencyAccess') { setEmergencyDialogOpen(true); return; }
                  setMhrAccessType(type);
                  loadHealthRecord(type);
                }}
                  disabled={isLoadingRecord}
                  style={{
                    ...btnStyle, width: 'auto', flex: 1, marginBottom: 0, fontSize: '10px', padding: '4px 8px',
                    cursor: isLoadingRecord ? 'default' : 'pointer',
                    opacity: isLoadingRecord ? 0.5 : 1,
                    background: mhrAccessType === type
                      ? (type === 'EmergencyAccess' ? 'rgba(220,38,38,0.22)' : 'rgba(99,102,241,0.22)')
                      : '#2a2a3a',
                    color: mhrAccessType === type ? (type === 'EmergencyAccess' ? '#f87171' : '#a5b4fc') : '#999'
                  }}>
                  {type === 'EmergencyAccess' ? 'Emergency access' : 'General access'}
                </button>
              ))}
            </div>

            {mhrEvents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '6px' }}>
                {mhrEvents.map(ev => {
                  const colour = {
                    ok: '#22c55e', pending: '#6366f1', error: '#ef4444',
                    skipped: '#666', cancelled: '#666', queued: '#3a3a4a'
                  }[ev.status] || '#3a3a4a';
                  return (
                    <div key={ev.id} title={`${ev.method} ${ev.path}`} style={{
                      display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px',
                      opacity: ev.status === 'queued' ? 0.45 : 1, transition: 'opacity 0.25s'
                    }}>
                      <span style={{
                        width: '5px', height: '5px', borderRadius: '50%', background: colour, flexShrink: 0,
                        animation: ev.status === 'pending' ? 'pulse 1.2s infinite' : 'none'
                      }} />
                      <span style={{ color: ev.status === 'error' ? '#ef4444' : '#bbb', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.label}
                      </span>
                      <span style={{ color: '#666', fontSize: '9px', whiteSpace: 'nowrap', maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.detail || ''}
                      </span>
                      {ev.durationMs != null && (
                        <span style={{ color: '#444', fontSize: '9px', whiteSpace: 'nowrap' }}>{ev.durationMs}ms</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {mhrError && (
              <div style={{ fontSize: '10px', color: '#ef4444', marginBottom: '6px' }}>
                {mhrError} — showing locally stored record.
              </div>
            )}

            {/* Data only the gateway provides */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[
                { key: 'pathology', label: `${mhrExtra.pathology.length} pathology results`, colour: '#eab308', tint: 'rgba(234,179,8,0.1)',
                  rows: mhrExtra.pathology.map(r => ({
                    left: r.name,
                    right: `${r.value} ${r.unit}`,
                    flag: r.interpretationCode !== 'N' ? r.interpretation : null,
                    sub: `ref ${r.referenceRange} · ${String(r.collected || '').slice(0, 10)}`
                  })) },
                { key: 'dispenses', label: `${mhrExtra.dispenses.length} dispense records`, colour: '#8b8bf5', tint: 'rgba(99,102,241,0.1)',
                  rows: mhrExtra.dispenses.map(d => ({
                    left: d.medication, right: String(d.dispensedOn || '').slice(0, 10), sub: `${d.quantity || ''} · ${d.dispenser || ''}`
                  })) },
                { key: 'documents', label: `${mhrExtra.documents.length} documents`, colour: '#22c55e', tint: 'rgba(34,197,94,0.1)',
                  rows: mhrExtra.documents.map(d => ({
                    left: d.title, right: String(d.created || '').slice(0, 10), sub: d.custodian
                  })) },
                { key: 'pbs', label: `${mhrExtra.pbs.length} PBS claims`, colour: '#38bdf8', tint: 'rgba(56,189,248,0.1)',
                  rows: mhrExtra.pbs.map(p => ({
                    left: p.description, right: String(p.date || '').slice(0, 10), sub: `Item ${p.itemCode} · $${p.benefit}`
                  })) }
              ].filter(group => group.rows.length > 0).map(group => (
                <div key={group.key}>
                  <div onClick={() => setMhrSectionOpen(p => ({ ...p, [group.key]: !p[group.key] }))}
                    style={{ cursor: 'pointer', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', background: group.tint, color: group.colour, fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{group.label}</span>
                    <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: mhrSectionOpen[group.key] ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
                  </div>
                  {mhrSectionOpen[group.key] && (
                    <div style={{ padding: '4px 6px', fontSize: '10px', color: '#ccc', lineHeight: '1.5' }}>
                      {group.rows.map((row, i) => (
                        <div key={i} style={{ marginBottom: '3px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                              <span style={{ color: group.colour }}>●</span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.left}</span>
                              {row.flag && (
                                <span style={{ color: '#ef4444', fontSize: '8px', fontWeight: '700', textTransform: 'uppercase' }}>{row.flag}</span>
                              )}
                            </span>
                            <span style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap' }}>{row.right}</span>
                          </div>
                          {row.sub && <div style={{ paddingLeft: '12px', fontSize: '9px', color: '#666' }}>{row.sub}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <Divider />

        {/* --- Health Record --- */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: healthRecordExpanded ? '8px' : '0' }}>
          <h3 onClick={() => setHealthRecordExpanded(e => !e)} style={{
            fontSize: '11px', color: '#666', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            Clinical summary
            <span style={{ fontSize: '9px', transition: 'transform 0.2s', transform: healthRecordExpanded ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isLoadingRecord && !healthRecord && (
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6366f1', animation: 'insightDot 1.4s infinite' }} />
            )}
            {!healthRecord && !isLoadingRecord && (
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: connectionStatus === 'live' ? '#eab308' : '#555' }} />
            )}
            {healthRecord && (
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }} />
            )}
            <span style={{ fontSize: '9px', color: healthRecord ? '#22c55e' : '#555' }}>
              {isLoadingRecord && !healthRecord ? 'Loading...' : healthRecord ? 'Active' : 'Pending'}
            </span>
          </div>
        </div>
        {healthRecordExpanded && (
          <>
        {isLoadingRecord && !healthRecord && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1', animation: 'insightDot 1.4s infinite' }} />
            <span style={{ fontSize: '11px', color: '#888' }}>Retrieving patient record...</span>
          </div>
        )}
        {!healthRecord && !isLoadingRecord && (
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '10px', color: '#555', lineHeight: '1.5' }}>
              {connectionStatus !== 'live' && <div>Patient video link required</div>}
              <div>Patient identity confirmation pending</div>
              <div>Consent verification pending</div>
            </div>
          </div>
        )}
        {healthRecord && (
          <div style={{ marginBottom: '8px' }}>
            {healthRecord.patient && (
              <div style={{ marginBottom: '8px' }}>
                <Row label="Name" value={healthRecord.patient.name ? `${healthRecord.patient.name.given || ''} ${healthRecord.patient.name.family || ''}`.trim() || null : null} />
                <Row label="DOB" value={healthRecord.patient.birthDate || 'Unknown'} />
                <Row label="Gender" value={healthRecord.patient.gender} />
                {healthRecord.patient.address && (
                  <Row label="Location" value={`${healthRecord.patient.address.city}, ${healthRecord.patient.address.state}`} />
                )}
              </div>
            )}
            {healthRecord.clinicalSections && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Allergies */}
                {healthRecord.clinicalSections.allergies?.length > 0 && (
                  <div>
                    <div onClick={() => setHrSectionOpen(p => ({ ...p, allergies: !p.allergies }))}
                      style={{ cursor: 'pointer', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', background: 'rgba(220,38,38,0.1)', color: '#ef4444', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{healthRecord.clinicalSections.allergies.length} {healthRecord.clinicalSections.allergies.length === 1 ? 'allergy' : 'allergies'}</span>
                      <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: hrSectionOpen.allergies ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
                    </div>
                    {hrSectionOpen.allergies && (
                      <div style={{ padding: '4px 6px', fontSize: '10px', color: '#ccc', lineHeight: '1.6' }}>
                        {healthRecord.clinicalSections.allergies.map((a, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: '#ef4444' }}>●</span>
                            {a.substance?.display || a.substance || 'Unknown'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Medications */}
                {healthRecord.clinicalSections.medications?.length > 0 && (
                  <div>
                    <div onClick={() => setHrSectionOpen(p => ({ ...p, medications: !p.medications }))}
                      style={{ cursor: 'pointer', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', background: 'rgba(99,102,241,0.1)', color: '#8b8bf5', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{healthRecord.clinicalSections.medications.length} medications</span>
                      <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: hrSectionOpen.medications ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
                    </div>
                    {hrSectionOpen.medications && (
                      <div style={{ padding: '4px 6px', fontSize: '10px', color: '#ccc', lineHeight: '1.6' }}>
                        {healthRecord.clinicalSections.medications.map((m, i) => (
                          <div key={i} style={{ marginBottom: '3px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#8b8bf5' }}>●</span>
                              <span style={{ fontWeight: '600', color: '#ddd' }}>{m.name}</span>
                            </div>
                            <div style={{ paddingLeft: '12px', fontSize: '9px', color: '#888' }}>{m.dose} — {m.frequency}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Medical History */}
                {healthRecord.clinicalSections.medicalHistory?.length > 0 && (
                  <div>
                    <div onClick={() => setHrSectionOpen(p => ({ ...p, history: !p.history }))}
                      style={{ cursor: 'pointer', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', background: 'rgba(234,179,8,0.1)', color: '#eab308', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{healthRecord.clinicalSections.medicalHistory.length} conditions</span>
                      <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: hrSectionOpen.history ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
                    </div>
                    {hrSectionOpen.history && (
                      <div style={{ padding: '4px 6px', fontSize: '10px', color: '#ccc', lineHeight: '1.6' }}>
                        {healthRecord.clinicalSections.medicalHistory.map((h, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#eab308' }}>●</span>
                              {h.condition}
                            </span>
                            <span style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap' }}>{h.date}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {/* Immunisations */}
                {healthRecord.clinicalSections.immunisations?.length > 0 && (
                  <div>
                    <div onClick={() => setHrSectionOpen(p => ({ ...p, immunisations: !p.immunisations }))}
                      style={{ cursor: 'pointer', fontSize: '9px', padding: '3px 6px', borderRadius: '4px', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{healthRecord.clinicalSections.immunisations.length} immunisations</span>
                      <span style={{ fontSize: '8px', transition: 'transform 0.2s', transform: hrSectionOpen.immunisations ? 'rotate(0)' : 'rotate(-90deg)' }}>▼</span>
                    </div>
                    {hrSectionOpen.immunisations && (
                      <div style={{ padding: '4px 6px', fontSize: '10px', color: '#ccc', lineHeight: '1.6' }}>
                        {healthRecord.clinicalSections.immunisations.map((im, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#22c55e' }}>●</span>
                              {im.vaccine}
                            </span>
                            <span style={{ fontSize: '9px', color: '#666', whiteSpace: 'nowrap' }}>{im.date}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
          </>
        )}

        <Divider />

        {/* LEVEL 2 — what this service already holds on the patient */}
        <RecordLevel
          level="2"
          title="EHR — service record"
          source="Held by RFDS · last clinical entry, not live"
          status={ehrRecord ? 'Loaded' : connectionStatus === 'live' ? 'Loading' : 'Idle'}
          statusColour={ehrRecord ? '#22c55e' : '#555'}
        >
          {ehrRecord ? (() => {
            const cs = ehrRecord.clinicalSections || {};
            const rows = [
              ['Allergies', (cs.allergies || []).map(a => a.substance?.display || a.substance)],
              ['Medications', (cs.medications || []).map(m => m.name)],
              ['Conditions', (cs.medicalHistory || []).map(h => h.condition)],
              ['Immunisations', (cs.immunisations || []).map(i => i.vaccine)]
            ].filter(([, v]) => v.length);
            if (!rows.length) return <div style={{ fontSize: '10px', color: '#555' }}>No clinical entries</div>;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px' }}>
                {rows.map(([label, values]) => (
                  <div key={label} style={{ display: 'flex', gap: '6px', alignItems: 'baseline' }}>
                    <span style={{ fontSize: '9px', color: '#555', fontWeight: 600, width: '76px', flexShrink: 0 }}>{label}</span>
                    <span style={{ fontSize: '10px', color: '#bbb', lineHeight: 1.45 }}>{values.join(', ')}</span>
                  </div>
                ))}
              </div>
            );
          })() : (
            <div style={{ fontSize: '10px', color: '#555', marginBottom: '8px' }}>
              {connectionStatus === 'live' ? 'Retrieving service record…' : 'Awaiting consult'}
            </div>
          )}
        </RecordLevel>


        <Divider />

        {/* --- Transcript --- */}
        <SidebarHeading>Transcript</SidebarHeading>
        <div style={{
          maxHeight: '180px', overflowY: 'auto', fontSize: '11px', lineHeight: '1.5',
          background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '8px',
          display: 'flex', flexDirection: 'column', gap: '4px'
        }}>
          {transcript.length === 0 ? (
            <span style={{ color: '#555', fontStyle: 'italic' }}>Waiting for audio...</span>
          ) : (
            transcript.slice(-30).map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px' }}>
                <span style={{
                  fontWeight: 600,
                  color: t.speaker === 'patient' ? '#38bdf8'
                    : t.speaker === 'matilda' ? '#6ee7b7'
                    : t.speaker === 'system' ? '#f97316'
                    : '#a78bfa',
                  minWidth: '60px', textTransform: 'capitalize', flexShrink: 0
                }}>{t.speaker === 'matilda' ? 'MATILDA' : t.speaker === 'system' ? 'SYSTEM' : t.speaker}</span>
                <span style={{ color: t.speaker === 'system' ? '#fed7aa' : '#ddd' }}>{t.text}</span>
              </div>
            ))
          )}
        </div>

        <Divider />

        {/* --- Session --- */}
        <SidebarHeading>Session</SidebarHeading>
        <Row label="Room" value={roomId} />
        <div style={{ marginTop: '4px' }}>
          {/* Invite. Replaces the old bare /join?room= link, which required the
              shared room password and so didn't match how patients now join. */}
          <span style={{ fontSize: '11px', color: '#666' }}>Invite patient</span>

          <form
            onSubmit={(e) => { e.preventDefault(); sendPatientLink(); }}
            style={{ display: 'flex', gap: '4px', marginTop: '4px' }}
          >
            <input
              type="tel"
              inputMode="tel"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              placeholder="0412 345 678"
              style={{
                flex: 1, minWidth: 0, padding: '6px 8px', borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.14)', background: '#1a1c28',
                color: '#fff', fontSize: '11px', outline: 'none'
              }}
            />
            <button
              type="submit"
              disabled={smsSending || !smsPhone.trim()}
              title="Text a single-use join link"
              style={{
                padding: '6px 10px', borderRadius: '4px', border: 'none', whiteSpace: 'nowrap',
                background: smsSending || !smsPhone.trim() ? 'rgba(37,99,235,0.35)' : '#2563eb',
                color: '#fff', fontSize: '11px', fontWeight: '600',
                cursor: smsSending || !smsPhone.trim() ? 'default' : 'pointer'
              }}
            >{smsSending ? '…' : 'SMS'}</button>
          </form>

          <button
            onClick={createInviteLink}
            disabled={inviteBusy}
            title="Create a single-use link and copy it, without sending an SMS"
            style={{
              width: '100%', marginTop: '4px', padding: '6px', borderRadius: '4px',
              border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
              color: '#c7d2e5', fontSize: '11px', fontWeight: '600',
              cursor: inviteBusy ? 'default' : 'pointer', opacity: inviteBusy ? 0.5 : 1
            }}
          >{inviteBusy ? 'Creating…' : 'Copy secure link'}</button>

          {(smsResult || inviteMsg) && (
            <div style={{
              marginTop: '5px', fontSize: '10px', lineHeight: '1.45',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              color: (inviteMsg ? inviteMsg.ok : smsResult.ok) ? '#6ee7a8' : '#ff8d99'
            }}>{inviteMsg ? inviteMsg.text : smsResult.message}</div>
          )}

          {inviteLink && (
            <code style={{
              display: 'block', padding: '6px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px',
              fontSize: '10px', color: '#6ee7b7', wordBreak: 'break-all', marginTop: '4px'
            }}>{inviteLink}</code>
          )}
        </div>
      </aside>

      {/* Insight Chat — integrated side panel */}
      <style>{`
        @keyframes insightSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes insightDot { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }
      `}</style>

      {!insightOpen && (
        <button onClick={() => setInsightOpen(true)} style={{
          position: 'fixed', bottom: '24px', right: '24px', width: '52px', height: '52px',
          borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', zIndex: 200,
          background: 'rgba(30,30,46,0.9)', backdropFilter: 'blur(8px)', color: '#888',
          fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)', transition: 'transform 0.2s, box-shadow 0.2s'
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.6)'; e.currentTarget.style.color = '#ccc'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.4)'; e.currentTarget.style.color = '#888'; }}
          title="Open Insight"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {insightOpen && (
          <div style={{
            width: '400px', flexShrink: 0, background: '#1a1a2e',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
            animation: 'insightSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => setInsightOpen(false)} style={{
                  background: 'transparent', border: 'none', color: '#888', cursor: 'pointer',
                  fontSize: '16px', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center'
                }}
                  onMouseEnter={e => e.currentTarget.style.color = '#ccc'}
                  onMouseLeave={e => e.currentTarget.style.color = '#888'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#fff', letterSpacing: '0.3px' }}>Insight</span>
                {/* What it can see. The value of this panel is the context it
                    already holds, so say so rather than making them guess. */}
                <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                  {[
                    ['Vitals', !!(clinicalVitals.bpm || clinicalVitals.breathingRate || clinicalVitals.spo2?.value)],
                    ['Triage', triageScore != null],
                    ['Record', !!healthRecord?.patient],
                    ['Scene', !!frameDescription]
                  ].map(([label, has]) => (
                    <span key={label} title={has ? `${label} is in context` : `No ${label.toLowerCase()} yet`} style={{
                      fontSize: '8.5px', fontWeight: 700, letterSpacing: '0.3px',
                      padding: '2px 6px', borderRadius: '999px',
                      background: has ? 'rgba(99,102,241,0.16)' : 'rgba(255,255,255,0.04)',
                      color: has ? '#a5b4fc' : '#4a4a55',
                      border: `1px solid ${has ? 'rgba(99,102,241,0.3)' : 'transparent'}`
                    }}>{label}</span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {insightMessages.length > 0 && (
                  <button onClick={() => { setInsightMessages([]); }} style={{
                    background: 'transparent', border: 'none', color: '#555', cursor: 'pointer',
                    fontSize: '11px', padding: '4px 8px', borderRadius: '4px'
                  }}
                    onMouseEnter={e => e.currentTarget.style.color = '#aaa'}
                    onMouseLeave={e => e.currentTarget.style.color = '#555'}
                  >New session</button>
                )}
              </div>
            </div>

            {/* Scrollable messages area */}
            <div style={{
              flexGrow: 1, minHeight: 0, overflowY: 'auto',
              // 16px, not 120px: the composer is a sibling below this, not an
              // overlay, so the old padding just left a screenful of dead space
              // under the last reply.
              padding: '16px',
              display: 'flex', flexDirection: 'column', gap: '14px'
            }}
              role="log"
              aria-live="polite"
              aria-label="Insight conversation"
            >
              {insightMessages.length === 0 && !insightLoading && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#555' }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '14px',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>Ask anything about this patient</span>
                  <span style={{ fontSize: '11px', color: '#444', textAlign: 'center', lineHeight: '1.5', maxWidth: '260px' }}>
                    I have context on vitals, triage, observations, and health records. Ask me to interpret, suggest, or reason.
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', justifyContent: 'center' }}>
                    {['What does the HR suggest?', 'Summarise findings', 'Any red flags?', 'Suggest next steps'].map(q => (
                      <button key={q} className="insight-suggest"
                        onClick={() => { setInsightInput(q); insightInputRef.current?.focus(); }}
                      >{q}</button>
                    ))}
                  </div>
                </div>
              )}

              {insightMessages.map((msg, i) => (
                msg.isOoda ? (
                  <OodaMessage key={i} msg={msg} isLatest={i === insightMessages.length - 1} />
                ) : (
                  <div key={i} style={{
                    display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-start', gap: '8px'
                  }}>
                    {msg.role === 'assistant' && (
                      <div style={{
                        width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0, marginTop: '2px',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="insight-bubble" style={{
                      maxWidth: '86%', padding: '11px 14px', borderRadius: '13px',
                      fontSize: '13px', lineHeight: '1.55',
                      ...(msg.role === 'user'
                        ? { background: '#6366f1', color: '#fff', borderBottomRightRadius: '4px', whiteSpace: 'pre-wrap' }
                        : msg.isError
                          ? { background: 'rgba(239,68,68,0.10)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)', borderBottomLeftRadius: '4px' }
                          : { background: 'rgba(255,255,255,0.045)', color: '#dcdce4', border: '1px solid rgba(255,255,255,0.07)', borderBottomLeftRadius: '4px' }
                      )
                    }}>
                      {msg.role === 'assistant' && msg.isError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', fontWeight: 700, fontSize: '11px', letterSpacing: '0.3px' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <circle cx="12" cy="12" r="10" /><path d="M12 8v5M12 16h.01" />
                          </svg>
                          NOT A CLINICAL RESPONSE
                        </div>
                      )}
                      {msg.role === 'assistant' && !msg.isError
                        ? <InsightMarkdown text={msg.content} />
                        : msg.content}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px',
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'space-between'
                      }}>
                        <span style={{ fontSize: '9px', color: msg.role === 'user' ? 'rgba(255,255,255,0.5)' : '#5a5a66' }}>
                          {msg.time}
                        </span>
                        {msg.role === 'assistant' && !msg.isError && (
                          <button
                            className="insight-copy"
                            title="Copy to clipboard"
                            onClick={() => {
                              navigator.clipboard?.writeText(msg.content);
                              setInsightCopied(i);
                              setTimeout(() => setInsightCopied(c => (c === i ? null : c)), 1600);
                            }}>
                            {insightCopied === i ? 'Copied' : 'Copy'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              ))}

              {insightLoading && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0, marginTop: '2px',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div style={{
                    padding: '12px 18px', borderRadius: '12px', borderBottomLeftRadius: '4px',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', gap: '4px'
                  }}>
                    {[0, 1, 2].map(d => (
                      <div key={d} style={{
                        width: '6px', height: '6px', borderRadius: '50%', background: '#6366f1',
                        animation: `insightDot 1.4s infinite`, animationDelay: `${d * 0.2}s`
                      }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={insightEndRef} />
            </div>

            {/* Input — pinned at bottom */}
            <form onSubmit={e => { e.preventDefault(); sendInsightMessage(); }} style={{
              padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0,
              background: 'rgba(26,26,46,0.95)', backdropFilter: 'blur(8px)'
            }}>
              <textarea
                ref={insightInputRef}
                value={insightInput}
                rows={1}
                onChange={e => {
                  setInsightInput(e.target.value);
                  // Grow with the question instead of hiding it in a one-line box.
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 132)}px`;
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendInsightMessage();
                    if (insightInputRef.current) insightInputRef.current.style.height = 'auto';
                  }
                }}
                placeholder="Ask about this patient…   (Shift + Enter for a new line)"
                className="insight-input"
              />
              <button type="submit" disabled={!insightInput.trim() || insightLoading} style={{
                width: '36px', height: '36px', borderRadius: '10px', border: 'none',
                background: insightInput.trim() ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.04)',
                color: insightInput.trim() ? '#fff' : '#555', cursor: insightInput.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </form>
          </div>
      )}

      </div>{/* end content row */}
    </div>
  );
}

function OverlayToggle({ label, active, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: '6px', border: `1px solid ${active ? color : '#555'}`,
      background: active ? `${color}22` : 'rgba(0,0,0,0.5)', color: active ? color : '#888',
      fontSize: '11px', fontWeight: '600', cursor: 'pointer', backdropFilter: 'blur(4px)',
      transition: 'all 0.15s'
    }}>
      {label}
    </button>
  );
}

// Compact identity verification card for the right-hand sidebar.
// Renders the enrolled face thumbnail next to a status pill with details
// from the server's periodic same-person check (GPT-4o-mini vision).
function IdentityCard({ thumbnail, status, confidence, reason, checkedAt, failures, absentSeconds, onReenrol }) {
  const palette = {
    enrolling: { color: '#9ca3af', label: 'Enrolling' },
    checking:  { color: '#38bdf8', label: 'Verifying' },
    verified:  { color: '#22c55e', label: 'Verified' },
    // Absence is amber and worded as absence. It is NOT a red identity failure:
    // a patient who steps out of shot has not become a different person, and
    // showing it as a mismatch trains clinicians to ignore the real thing.
    absent:    { color: '#eab308', label: 'Not in frame' },
    mismatch:  { color: '#ef4444', label: 'Different person' },
    unknown:   { color: '#eab308', label: 'Unverified' },
    error:     { color: '#9ca3af', label: 'Check failed' }
  };
  const tone = palette[status] || palette.unknown;
  const ageMs = checkedAt ? Date.now() - checkedAt : null;
  const ageStr = ageMs == null ? null : ageMs < 60000 ? `${Math.round(ageMs / 1000)}s ago` : `${Math.round(ageMs / 60000)}m ago`;

  let detail = null;
  if (status === 'verified' && confidence != null) {
    detail = `Match confidence ${Math.round(confidence * 100)}%${ageStr ? ` · ${ageStr}` : ''}`;
  } else if (status === 'mismatch') {
    detail = failures > 1 ? `${failures} mismatches in a row` : 'Live face does not match enrolled patient';
  } else if (status === 'absent') {
    detail = absentSeconds
      ? `No one on camera for ${absentSeconds < 60 ? `${absentSeconds}s` : `${Math.round(absentSeconds / 60)}m`} · verification paused`
      : 'No one on camera · verification paused';
  } else if (status === 'enrolling') {
    detail = 'Capturing reference face…';
  } else if (status === 'checking' && !confidence) {
    detail = 'Checking against enrolled face…';
  } else if ((status === 'unknown' || status === 'error') && reason) {
    detail = reason;
  }

  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '10px', overflow: 'hidden',
          background: '#0f172a', flexShrink: 0,
          border: `2px solid ${tone.color}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative'
        }}>
          {thumbnail ? (
            <img src={thumbnail} alt="Enrolled patient" style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: 'scaleX(-1)'
            }} />
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
            </svg>
          )}
          <span style={{
            position: 'absolute', bottom: 4, right: 4,
            width: 8, height: 8, borderRadius: '50%', background: tone.color,
            border: '1.5px solid #0f172a',
            animation: status === 'checking' ? 'identityPulse 1.4s ease-in-out infinite' : 'none'
          }} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
            <span style={{ fontSize: '13px', color: tone.color, fontWeight: 700 }}>{tone.label}</span>
            {status === 'verified' && confidence != null && (
              <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', color: '#888', fontWeight: 600 }}>
                {Math.round(confidence * 100)}%
              </span>
            )}
            {ageStr && status !== 'verified' && (
              <span style={{ fontSize: '10px', color: '#555', marginLeft: 'auto' }}>{ageStr}</span>
            )}
          </div>
          {detail && (
            <p style={{ fontSize: '11px', color: status === 'mismatch' ? '#fecaca' : '#bbb', margin: '0 0 6px', lineHeight: 1.4 }}>
              {detail}
            </p>
          )}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={onReenrol} title="Treat the current view as a new patient" style={{
              fontSize: '10px', padding: '3px 8px', borderRadius: '6px',
              background: 'rgba(255,255,255,0.04)', color: '#cbd5e1',
              border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', fontWeight: 600
            }}>{thumbnail ? 'Re-enrol' : 'Enrol'}</button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes identityPulse {
          0%, 100% { opacity: 0.5; transform: scale(0.9); }
          50% { opacity: 1; transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}

/**
 * One control in the dock across the bottom of the video.
 *
 * Styling lives in App.css rather than inline so the thing can actually have
 * hover, press and focus states — the previous inline version declared a
 * transition but had nothing to transition to, so the controls never answered
 * the pointer. The accent colour is handed to CSS as a custom property.
 */
function ControlBtn({ onClick, disabled, active, activeColor = '#2563eb', icon, label, danger, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      aria-pressed={active ? true : undefined}
      aria-label={label}
      className={`ctrl-btn${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}`}
      style={{ '--ctrl-accent': activeColor }}
    >
      <span className="ctrl-icon">{icon}</span>
      <span className="ctrl-label">{label}</span>
    </button>
  );
}

// Render a field the LLM was asked to return as a string array. The server
// normalises these, but a render path must never crash on model output: a
// non-array `equipment_states` took the whole console down with
// "join is not a function" mid-render, which React escalates to a blank screen.
function joinList(value, sep = ', ') {
  if (value == null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join(sep);
  if (typeof value === 'object') return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(sep);
  return String(value);
}

// m:ss countdown, matching the patient's escalation timer.
function formatClock(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Coarse minutes for flight ETA — seconds would be false precision on a 40-min leg.
function formatEtaMinutes(totalSeconds) {
  const mins = Math.ceil(Math.max(0, totalSeconds) / 60);
  if (mins <= 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// --- Control icons (SVG, 16px, currentColor for theming) ---
const iconBase = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
function OnCallIcon() {
  return (
    <svg {...iconBase}>
      <path d="M15.5 3.5a5 5 0 0 1 5 5" />
      <path d="M14.5 7a2.5 2.5 0 0 1 2.5 2.5" />
      <path d="M11 5.5 8.5 3 4 6c0 6.5 5.5 12 12 12l3-4.5-2.5-2.5-2.5 2A14 14 0 0 1 9 8z" />
    </svg>
  );
}
function SmsIcon() {
  return (
    <svg {...iconBase}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function EndCallIcon() {
  return (
    <svg {...iconBase}>
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
function PlaneIcon() {
  return (
    <svg {...iconBase}>
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.4-.2c.4-.2.7-.6.6-1.1z" />
    </svg>
  );
}
function ScanIcon() {
  return (
    <svg {...iconBase}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  );
}
function SpinnerIcon() {
  return (
    <svg {...iconBase} style={{ animation: 'ctrlSpin 0.9s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
function AutoIcon({ active }) {
  return (
    <svg {...iconBase}>
      <circle cx="12" cy="12" r="9" />
      {active && <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />}
    </svg>
  );
}
function MicOnIcon() {
  return (
    <svg {...iconBase}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  );
}
function MicOffIcon() {
  return (
    <svg {...iconBase}>
      <line x1="3" y1="3" x2="21" y2="21" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
      <path d="M19 11a7 7 0 0 1-1.21 3.93" />
      <path d="M5 11a7 7 0 0 0 11 5.66" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  );
}
function BotIcon() {
  return (
    <svg {...iconBase}>
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <line x1="12" y1="4" x2="12" y2="8" />
      <circle cx="12" cy="3" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="14" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="14" r="1" fill="currentColor" stroke="none" />
      <line x1="2" y1="14" x2="4" y2="14" />
      <line x1="20" y1="14" x2="22" y2="14" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg {...iconBase}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/**
 * One of the three levels of patient information the console distinguishes.
 *
 * Provenance is clinical: the national record, the service's own record and
 * what the camera measured in the last few minutes carry different weight and
 * different age, and a clinician acting on a fact needs to know which it came
 * from. They are never merged into one undifferentiated list.
 */
function RecordLevel({ level, title, source, logo, status, statusColour, children }) {
  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
        <span style={{
          fontSize: '8px', fontWeight: 800, color: '#555', background: 'rgba(255,255,255,0.06)',
          borderRadius: '3px', padding: '2px 5px', flexShrink: 0, letterSpacing: '0.4px'
        }}>
          {level}
        </span>
        {logo && (
          <img src={logo} alt="" aria-hidden="true"
            style={{ height: '15px', width: 'auto', flexShrink: 0, display: 'block' }} />
        )}
        <h3 style={{
          fontSize: '11px', color: '#888', margin: 0, textTransform: 'uppercase',
          letterSpacing: '0.5px', fontWeight: 700, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {title}
        </h3>
        {status && (
          <span style={{ fontSize: '9px', color: statusColour || '#555', whiteSpace: 'nowrap', fontWeight: 600 }}>
            {status}
          </span>
        )}
      </div>
      {source && (
        <div style={{ fontSize: '9px', color: '#4a4a55', marginBottom: '6px', lineHeight: 1.45 }}>{source}</div>
      )}
      {children}
    </div>
  );
}

function SidebarHeading({ children }) {
  return (
    <h3 style={{ fontSize: '11px', color: '#666', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' }}>
      {children}
    </h3>
  );
}

// The rPPG intermediate stages, as a clinician-readable view.
//
// All four of these have existed in python/heart_rate_analysis.py for a long
// time, drawn as 106x240 thumbnails into an OpenCV composite that was then
// stripped from the WebSocket broadcast, so nothing ever rendered them. The
// chest-motion trace and the peak intervals are numbers a clinician reads
// values off, so they now travel as numbers and are drawn at whatever size the
// panel is. ROI / Gaussian / red-channel are genuinely pixels and stay JPEGs --
// gated off until this view is open, because the link may be satellite.
function SignalDiagnostics({ dataRef, diag, spectrum, bpm, open, onToggle, panelsOn, onPanelsToggle }) {
  // The chest trace arrives on the per-frame overlay channel and is held in a
  // ref so the console does not re-render for it. Poll that ref here, and only
  // while the section is open: between Python's updates the ref holds the SAME
  // object, so React bails out on identity and only a genuinely new trace
  // causes a render -- of this component alone, not the console.
  const [trace, setTrace] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    setTrace(dataRef.current.breathing);
    const t = setInterval(() => setTrace(dataRef.current.breathing), 150);
    return () => clearInterval(t);
  }, [open, dataRef]);

  const sig = trace?.signal || [];
  const peaks = trace?.peaks || [];
  const intervals = trace?.intervals || [];
  const panels = diag?.panels || {};
  const freqs = spectrum?.frequencies || [];
  const amps = spectrum?.amplitudes || [];
  const hasSignal = sig.length > 1;
  const hasSpectrum = amps.some(a => a > 0);
  // Visibility keys off the ref, not the polled copy -- otherwise the header
  // would be hidden while collapsed and there would be no way to open it.
  const hasTrace = hasSignal || !!dataRef.current?.breathing?.signal?.length;
  if (!hasTrace && !hasSpectrum) return null;

  const W = 268, H = 46;
  const xAt = (i) => (i / Math.max(sig.length - 1, 1)) * W;
  const yAt = (i) => H - 4 - (sig[i] * (H - 10));
  // Only when expanded: this is a 300-point map+join and the console re-renders
  // on every vitals tick whether or not anyone has this section open.
  const path = (open && hasSignal)
    ? sig.map((_, i) => (i ? 'L' : 'M') + xAt(i).toFixed(1) + ' ' + yAt(i).toFixed(1)).join(' ')
    : '';
  const meanInterval = intervals.length
    ? intervals.reduce((a, b) => a + b, 0) / intervals.length
    : null;
  // Peak-to-peak seconds is the same quantity as RR, only unsmoothed -- so it is
  // the one number here that can visibly contradict the published rate.
  const impliedRr = meanInterval ? Math.round(60 / meanInterval) : null;

  const label = { fontSize: '9px', color: '#555', fontWeight: '600', letterSpacing: '0.4px' };

  return (
    <div style={{ marginTop: '4px' }}>
      <button onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
        background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer'
      }}>
        <span style={{ ...label, color: '#4b5565' }}>SIGNAL</span>
        <span style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
        <span style={{ fontSize: '9px', color: '#444' }}>{open ? 'hide' : 'show'}</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', paddingBottom: '4px' }}>
          {hasSignal && (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '2px' }}>
                <span style={label}>CHEST MOTION</span>
                {impliedRr != null && (
                  <span style={{ fontSize: '9px', color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>
                    {meanInterval.toFixed(1)}s peak-to-peak = {impliedRr} br/m
                  </span>
                )}
              </div>
              <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: '46px', display: 'block' }}>
                <path d={path} fill="none" stroke="#3b82f6" strokeWidth="1.2" />
                {peaks.map(i => (
                  <circle key={i} cx={xAt(i)} cy={yAt(i)} r="2" fill="#eab308" />
                ))}
              </svg>
            </div>
          )}

          {intervals.length > 0 && (
            <div>
              <div style={{ ...label, marginBottom: '3px' }}>PEAK INTERVALS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                {intervals.map((v, i) => (
                  <span key={i} style={{
                    fontSize: '9px', color: '#8b93a7', fontVariantNumeric: 'tabular-nums',
                    padding: '1px 5px', borderRadius: '4px', background: 'rgba(234,179,8,0.12)'
                  }}>{v.toFixed(1)}s</span>
                ))}
              </div>
            </div>
          )}

          {hasSpectrum && (
            <div>
              <div style={{ ...label, marginBottom: '2px' }}>PULSE SPECTRUM</div>
              <svg viewBox={'0 0 ' + W + ' 30'} style={{ width: '100%', height: '30px', display: 'block' }}>
                {amps.map((a, i) => {
                  const bw = W / amps.length;
                  const h = Math.max((a / 100) * 26, 0.4);
                  // Mark where the published rate sits, so a spectrum peaking
                  // somewhere else is visible here rather than only in the logs.
                  const atPublished = bpm > 0 && freqs[i] != null && Math.abs(freqs[i] - bpm) <= 2;
                  return (
                    <rect key={i} x={i * bw} y={28 - h} width={Math.max(bw - 1, 0.8)} height={h}
                      fill={atPublished ? '#ef4444' : '#37475c'} />
                  );
                })}
              </svg>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: '#3f4854' }}>
                <span>{freqs[0] ?? 45} bpm</span>
                <span>{freqs[freqs.length - 1] ?? 150} bpm</span>
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
              <span style={label}>STAGES</span>
              <button onClick={() => onPanelsToggle(!panelsOn)} style={{
                fontSize: '9px', padding: '1px 6px', borderRadius: '4px', cursor: 'pointer',
                background: panelsOn ? 'rgba(59,130,246,0.18)' : 'rgba(120,120,130,0.14)',
                color: panelsOn ? '#3b82f6' : '#777', border: 'none'
              }}>{panelsOn ? 'on' : 'off'}</button>
            </div>
            {panelsOn && (
              <div style={{ display: 'flex', gap: '4px' }}>
                {[['roi', 'ROI'], ['gauss', 'Gaussian'], ['redshift', 'Red shift']].map(([k, name]) => (
                  <div key={k} style={{ flex: 1, minWidth: 0 }}>
                    {panels[k]
                      ? <img src={panels[k]} alt={name} style={{ width: '100%', borderRadius: '3px', display: 'block' }} />
                      : <div style={{ width: '100%', paddingTop: '75%', background: 'rgba(255,255,255,0.03)', borderRadius: '3px' }} />}
                    <div style={{ fontSize: '8px', color: '#3f4854', textAlign: 'center', marginTop: '1px' }}>{name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '14px 0' }} />;
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '1px 0' }}>
      <span style={{ fontSize: '12px', color: '#666' }}>{label}</span>
      <span style={{ fontSize: '12px', color: '#ccc', fontWeight: '500', textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

function ABCDEBar({ findings, overallPct }) {
  const [expanded, setExpanded] = React.useState(null);

  const domainKeys = [
    { key: 'A', field: 'airway', label: 'Airway' },
    { key: 'B', field: 'breathing', label: 'Breathing' },
    { key: 'C', field: 'circulation', label: 'Circulation' },
    { key: 'D', field: 'disability', label: 'Disability' },
    { key: 'E', field: 'exposure', label: 'Exposure' },
  ];

  if (!findings) {
    return (
      <div style={{ marginBottom: '4px' }}>
        <div style={{ display: 'flex', gap: '3px', marginBottom: '4px' }}>
          {domainKeys.map(d => (
            <div key={d.key} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '3px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)', marginBottom: '3px' }} />
              <span style={{ fontSize: '9px', fontWeight: '600', color: '#444' }}>{d.key}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const pctColor = pct => pct >= 70 ? '#22c55e' : pct >= 40 ? '#eab308' : '#ef4444';

  return (
    <div style={{ marginBottom: '4px' }}>
      <div style={{ display: 'flex', gap: '3px', marginBottom: '6px' }}>
        {domainKeys.map(d => {
          const f = findings[d.field];
          const pct = f ? parseInt(f.completeness_percentage) || 0 : 0;
          const isOpen = expanded === d.field;
          return (
            <div key={d.key} style={{
              flex: 1, textAlign: 'center', cursor: f ? 'pointer' : 'default',
              padding: '3px 0', borderRadius: '4px', transition: 'background 0.2s',
              background: isOpen ? 'rgba(255,255,255,0.04)' : 'transparent'
            }}
              onClick={() => f && setExpanded(isOpen ? null : d.field)}>
              <div style={{
                height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)', marginBottom: '3px', overflow: 'hidden'
              }}>
                <div style={{ height: '100%', borderRadius: '2px', width: `${pct}%`, background: pctColor(pct), transition: 'width 0.5s ease' }} />
              </div>
              <span style={{
                fontSize: '9px', fontWeight: '600', letterSpacing: '0.3px',
                color: isOpen ? '#fff' : (pct >= 70 ? '#22c55e' : pct > 0 ? '#eab308' : '#444'),
                textDecoration: f ? 'none' : 'none'
              }}>{d.key}</span>
              {f && <span style={{ fontSize: '7px', color: '#555', display: 'block', marginTop: '1px' }}>▼</span>}
            </div>
          );
        })}
      </div>

      {expanded && findings[expanded] && (() => {
        const f = findings[expanded];
        const d = domainKeys.find(dk => dk.field === expanded);
        return (
          <div style={{
            padding: '8px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px',
            marginBottom: '6px', animation: 'insightSlideIn 0.2s ease', textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: '#ccc', fontWeight: '600' }}>{d.label}</span>
              <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: `${pctColor(f.completeness_percentage)}15`, color: pctColor(f.completeness_percentage), fontWeight: '600' }}>
                {f.completeness_percentage}%
              </span>
            </div>
            {f.status && (
              <p style={{ fontSize: '11px', color: '#999', margin: '0 0 4px', lineHeight: '1.4' }}>{f.status}</p>
            )}
            {expanded === 'disability' && (f.gcs != null || f.pain_score != null || f.avpu || f.temperature != null) && (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                {f.gcs != null && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99,102,241,0.1)', color: '#8b8bf5', fontWeight: '600' }}>GCS: {f.gcs}</span>}
                {f.avpu && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99,102,241,0.1)', color: '#8b8bf5', fontWeight: '600' }}>AVPU: {f.avpu}</span>}
                {f.pain_score != null && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(234,179,8,0.1)', color: '#eab308', fontWeight: '600' }}>Pain: {f.pain_score}/10</span>}
                {f.new_pain && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: f.new_pain === 'Y' ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', color: f.new_pain === 'Y' ? '#ef4444' : '#22c55e', fontWeight: '600' }}>New Pain: {f.new_pain}</span>}
                {f.temperature != null && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99,102,241,0.1)', color: '#8b8bf5', fontWeight: '600' }}>Temp: {f.temperature}°C</span>}
                {f.blood_glucose != null && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99,102,241,0.1)', color: '#8b8bf5', fontWeight: '600' }}>BGL: {f.blood_glucose}</span>}
              </div>
            )}
            {f.outstanding_questions?.length > 0 && (
              <div>
                <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: '500' }}>Outstanding:</span>
                {f.outstanding_questions.map((q, i) => (
                  <p key={i} style={{ fontSize: '10px', color: '#888', margin: '2px 0 2px 6px', lineHeight: '1.4' }}>- {q}</p>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function VitalCard({ label, value, unit, icon, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '12px', background: '#1e1e2d', borderRadius: '10px', marginBottom: '8px'
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', color: color
      }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '10px', color: '#666', marginBottom: '1px' }}>{label}</div>
        <div style={{ fontSize: '18px', fontWeight: '700', color: value ? '#fff' : '#444' }}>
          {value || '—'}
          {value && <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>{unit}</span>}
        </div>
      </div>
    </div>
  );
}

function OodaMessage({ msg, isLatest }) {
  const [expanded, setExpanded] = React.useState(false);
  const [collapseHeight, setCollapseHeight] = React.useState(0);
  const contentRef = React.useRef(null);
  const steps = msg.reasoning;

  React.useEffect(() => {
    if (contentRef.current) setCollapseHeight(contentRef.current.scrollHeight);
  }, [expanded, steps]);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <div style={{
        width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0, marginTop: '2px',
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Collapsible thought process */}
        {steps && (
          <div style={{ marginBottom: '6px', position: 'relative', borderLeft: '2px solid rgba(99,102,241,0.25)', paddingLeft: '10px' }}>
            <div
              onClick={() => setExpanded(e => !e)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                padding: '5px 0', userSelect: 'none'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#6366f1" style={{ flexShrink: 0, opacity: 0.6 }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14h2v-2h-2v2zm0-4h2V6h-2v6z" />
              </svg>
              <span style={{ fontSize: '11px', color: '#8b8bf5', fontWeight: '600' }}>Thought process</span>
              <span style={{ fontSize: '9px', color: '#555', marginLeft: 'auto' }}>3 steps</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'transform 0.3s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0, opacity: 0.6 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {/* Animated collapse container */}
            <div style={{
              maxHeight: expanded ? `${collapseHeight}px` : '0px',
              overflow: 'hidden',
              transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: expanded ? 1 : 0,
            }}>
              <div ref={contentRef} style={{ padding: '4px 0 4px 0' }}>
                {[
                  { label: 'OBSERVE', text: steps.observe, color: '#3b82f6' },
                  // Between OBSERVE and ORIENT, because that is where it sits in
                  // the loop: the model writes what it sees, asks one question,
                  // and the answers are injected before it interprets anything.
                  // Showing it anywhere else would misrepresent the reasoning.
                  { label: 'KNOWLEDGE BANK', kb: msg.knowledgeBank, color: '#8b5cf6' },
                  { label: 'ORIENT', text: steps.orient, color: '#eab308' },
                  { label: 'DECIDE', text: steps.decide, color: '#f97316' }
                ].filter(s => s.kb ? s.kb.asked : s.text).map((s, idx, all) => (
                  <div key={s.label} style={{ marginBottom: idx < all.length - 1 ? '10px' : '4px' }}>
                    <span style={{
                      fontSize: '9px', fontWeight: '700', color: s.color, letterSpacing: '0.5px',
                      textTransform: 'uppercase', display: 'inline-block', marginBottom: '3px',
                      padding: '1px 6px', borderRadius: '3px',
                      background: `${s.color}12`
                    }}>{s.label}</span>
                    {s.kb ? (
                      <div>
                        {/* The question is diagnostic in its own right: a poor
                            retrieval is usually a poor question, and you cannot
                            see that unless the question is shown. */}
                        <p style={{ fontSize: '11px', color: '#9b8cc4', margin: '0 0 5px', lineHeight: '1.5', fontStyle: 'italic' }}>
                          {s.kb.question}
                        </p>
                        {s.kb.results?.length ? s.kb.results.map((r, i) => (
                          <div key={r.entryId || i} style={{ display: 'flex', gap: '7px', marginBottom: '4px' }}>
                            {/* The similarity score, shown because a 0.52 match
                                and a 0.79 match are not the same claim strength
                                and should not look alike on a clinical screen. */}
                            <span style={{
                              flexShrink: 0, fontSize: '9px', fontWeight: '700',
                              color: r.score >= 0.65 ? '#a78bfa' : '#6b7280',
                              fontVariantNumeric: 'tabular-nums', paddingTop: '1px'
                            }}>{r.score.toFixed(2)}</span>
                            <span style={{ fontSize: '11px', color: '#aaa', lineHeight: '1.5' }}>{r.content}</span>
                          </div>
                        )) : (
                          <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, fontStyle: 'italic' }}>
                            Nothing in the knowledge bank was relevant enough to use.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p style={{ fontSize: '11px', color: '#aaa', margin: 0, lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>
                        {s.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action / final answer — always visible */}
        <div style={{
          padding: '10px 14px', borderRadius: '12px', borderBottomLeftRadius: '4px',
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
          fontSize: '13px', lineHeight: '1.55', color: '#ddd', whiteSpace: 'pre-wrap'
        }}>
          {msg.content}
          <div style={{ fontSize: '9px', color: '#555', marginTop: '4px' }}>{msg.time}</div>
        </div>
      </div>
    </div>
  );
}

export default ClinicianConsole;
